import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    ShieldCheck, Inbox, Mail, Users, Boxes, Image as ImageIcon,
    Plus, Trash2, Loader2, X as XIcon, ArrowUp, ArrowDown,
    DollarSign, FileDown, LogOut, KeyRound, Copy as CopyIcon, UserCog, History,
    User as UserIcon,
} from 'lucide-react';
import SEO from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';
import {
    apiAdminQuotes, apiAdminQuotePatch, apiAdminContacts,
    apiAdminClients, apiAdminCreateClient, apiAdminUpdateClient,
    apiAdminLeases, apiAdminCreateLease, apiAdminUpdateLease, apiAdminDeleteLease,
    apiAdminGetPricing, apiAdminPatchPricing, apiAdminExportLogs,
    apiAdminUploadImage,
    apiAdminUsers, apiAdminCreateUser, apiAdminUpdateUser,
    apiAdminResetUserPassword, apiAdminDeleteUser, apiAdminAuditLog,
    apiAdminContentSchema, apiAdminGetSection, apiAdminPutSection,
    apiClient,
} from '../lib/api';

const TABS = [
    { key: 'leads', label: 'Leads (Quotes)', icon: Inbox },
    { key: 'contacts', label: 'Contacts', icon: Mail },
    { key: 'clients', label: 'Clients', icon: Users },
    // 'leases' tab retired as part of the manufacturer-only repositioning
    // (Centurion does not lease). The LeasesView component below and the
    // /api/admin/leases backend routes stay in place so historical lease
    // records remain accessible if ever needed, but the tab is no longer
    // shown in the admin nav. Boxes icon import retained for the component.
    { key: 'hero', label: 'Hero Slides', icon: ImageIcon },
    { key: 'pricing', label: 'Pricing Settings', icon: DollarSign },
    { key: 'exports', label: 'Export Logs', icon: FileDown },
    // Phase 1 auth hardening: staff user management + audit trail.
    { key: 'users', label: 'Users', icon: UserCog },
    { key: 'content', label: 'Content (CMS)', icon: FileDown },
    { key: 'audit', label: 'Audit Log', icon: History },
];

const fmtDate = (s) =>
    s ? new Date(s).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }) : '-';

const fmtDateTime = (s) =>
    s ? new Date(s).toLocaleString('en-CA', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

export default function Admin() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState('leads');

    const onLogout = async () => {
        await logout();
        navigate('/login', { replace: true });
    };

    return (
        <>
            <SEO title="Admin Portal" description="Centurion admin portal." path="/admin" noindex />
            <div className="grid min-h-[calc(100vh-160px)] grid-cols-1 lg:grid-cols-[260px_1fr]">
                {/* Sidebar */}
                <aside className="border-r border-brand-hairline bg-brand-surface" data-testid="admin-sidebar">
                    <div className="border-b border-brand-hairline px-6 py-6">
                        <span className="inline-flex items-center gap-2 font-display text-[10px] font-bold uppercase tracking-[0.2em] text-brand-gold">
                            <ShieldCheck size={14} /> Admin Portal
                        </span>
                        <p className="mt-3 font-display text-base font-bold uppercase tracking-tight text-brand-text">
                            {user?.name}
                        </p>
                        <p className="mt-1 font-sans text-xs text-brand-muted">{user?.email}</p>
                        <div className="mt-4 flex items-center gap-2">
                            <Link
                                to="/admin/change-password"
                                data-testid="admin-change-password-link"
                                className="inline-flex items-center gap-1 border border-brand-hairline px-3 py-2 font-display text-[10px] font-bold uppercase tracking-[0.14em] text-brand-muted transition-colors hover:border-brand-gold hover:text-brand-gold"
                            >
                                <KeyRound size={11} /> Change PW
                            </Link>
                            <button
                                type="button"
                                onClick={onLogout}
                                data-testid="admin-logout"
                                className="inline-flex items-center gap-1 border border-brand-hairline px-3 py-2 font-display text-[10px] font-bold uppercase tracking-[0.14em] text-brand-muted transition-colors hover:border-brand-danger hover:text-brand-danger"
                            >
                                <LogOut size={11} /> Sign out
                            </button>
                        </div>
                    </div>
                    <nav className="flex flex-row overflow-x-auto lg:flex-col">
                        {TABS.map((t) => {
                            const Icon = t.icon;
                            const active = tab === t.key;
                            return (
                                <button
                                    key={t.key}
                                    type="button"
                                    onClick={() => setTab(t.key)}
                                    data-testid={`admin-tab-${t.key}`}
                                    className={[
                                        'flex items-center gap-3 whitespace-nowrap border-b border-brand-hairline px-6 py-4 text-left font-display text-xs font-bold uppercase tracking-[0.16em] transition-colors',
                                        active ? 'bg-brand-elevated text-brand-gold' : 'text-brand-muted hover:bg-brand-elevated hover:text-brand-text',
                                    ].join(' ')}
                                >
                                    <Icon size={14} /> {t.label}
                                </button>
                            );
                        })}
                    </nav>
                </aside>

                {/* Main */}
                <main className="bg-brand-base p-6 md:p-10" data-testid="admin-main">
                    {tab === 'leads' ? <LeadsView /> : null}
                    {tab === 'contacts' ? <ContactsView /> : null}
                    {tab === 'clients' ? <ClientsView /> : null}
                    {tab === 'leases' ? <LeasesView /> : null}
                    {tab === 'hero' ? <HeroView /> : null}
                    {tab === 'pricing' ? <PricingView /> : null}
                    {tab === 'exports' ? <ExportLogsView /> : null}
                    {tab === 'users' ? <UsersView currentUser={user} /> : null}
                    {tab === 'content' ? <ContentView /> : null}
                    {tab === 'audit' ? <AuditLogView /> : null}
                </main>
            </div>
        </>
    );
}

// --- Leads (Quotes) -------------------------------------------------------

const LEAD_STATUSES = ['new', 'contacted', 'won', 'lost'];

function LeadsView() {
    const [rows, setRows] = useState(null);
    const [drawer, setDrawer] = useState(null);

    const reload = () =>
        apiAdminQuotes()
            .then(setRows)
            .catch(() => setRows([]));
    useEffect(() => { reload(); }, []);

    const onPatch = async (id, body) => {
        await apiAdminQuotePatch(id, body);
        reload();
        if (drawer && drawer.id === id) setDrawer({ ...drawer, ...body });
    };

    return (
        <Section title="Leads (Quotes)" subtitle={`${rows?.length || 0} total`}>
            {rows === null ? <p className="text-brand-muted">Loading…</p> : (
                <Table
                    data-testid="admin-leads-table"
                    headers={['Created', 'Project', 'Company', 'Contact', 'Mat', 'Qty', 'Source', 'Status']}
                    rows={rows.map((q) => ({
                        key: q.id,
                        onClick: () => setDrawer(q),
                        cells: [
                            fmtDate(q.created_at),
                            q.project_name,
                            q.company,
                            <span><div>{q.contact_name}</div><div className="text-xs text-brand-muted">{q.email}</div></span>,
                            q.mat_type,
                            q.estimated_qty?.toLocaleString() || '-',
                            <SourceChip source={q.source} quoteNumber={q.quote_number} />,
                            <Chip color={q.lead_status}>{q.lead_status || 'new'}</Chip>,
                        ],
                    }))}
                />
            )}
            {drawer && (
                <Drawer onClose={() => setDrawer(null)} title={drawer.project_name}>
                    <KV k="Company" v={drawer.company} />
                    <KV k="Contact" v={`${drawer.contact_name} • ${drawer.email} • ${drawer.phone}`} />
                    <KV k="Mat / Qty" v={`${drawer.mat_type} • ${drawer.estimated_qty || '-'}`} />
                    <KV k="Project Type" v={drawer.project_type} />
                    <KV k="Province" v={drawer.province} />
                    <KV k="Created" v={fmtDate(drawer.created_at)} />
                    {drawer.notes ? <KV k="Notes" v={drawer.notes} /> : null}

                    <div className="mt-6 border-t border-brand-hairline pt-6">
                        <Label>Lead Status</Label>
                        <div className="mt-3 flex flex-wrap gap-2">
                            {LEAD_STATUSES.map((s) => (
                                <button
                                    key={s}
                                    type="button"
                                    data-testid={`admin-lead-status-${s}`}
                                    onClick={() => onPatch(drawer.id, { lead_status: s })}
                                    className={[
                                        'border px-3 py-1.5 font-display text-[10px] font-bold uppercase tracking-[0.18em]',
                                        (drawer.lead_status || 'new') === s
                                            ? 'border-brand-gold bg-brand-gold/10 text-brand-gold'
                                            : 'border-brand-hairline text-brand-muted hover:text-brand-text',
                                    ].join(' ')}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="mt-6 border-t border-brand-hairline pt-6">
                        <Label>Admin Notes</Label>
                        <textarea
                            defaultValue={drawer.admin_notes || ''}
                            data-testid="admin-lead-notes"
                            onBlur={(e) => onPatch(drawer.id, { admin_notes: e.target.value })}
                            rows={4}
                            className="mt-3 w-full border border-brand-hairline bg-brand-elevated p-3 font-sans text-sm text-brand-text outline-none focus:border-brand-gold"
                        />
                    </div>
                </Drawer>
            )}
        </Section>
    );
}

// --- Contacts -------------------------------------------------------------

function ContactsView() {
    const [rows, setRows] = useState(null);
    const [drawer, setDrawer] = useState(null);
    useEffect(() => { apiAdminContacts().then(setRows).catch(() => setRows([])); }, []);
    return (
        <Section title="Contacts" subtitle={`${rows?.length || 0} total`}>
            {rows === null ? <p className="text-brand-muted">Loading…</p> : (
                <Table
                    data-testid="admin-contacts-table"
                    headers={['Created', 'Name', 'Email', 'Topic', 'Company']}
                    rows={rows.map((c) => ({
                        key: c.id,
                        onClick: () => setDrawer(c),
                        cells: [fmtDate(c.created_at), c.name, c.email, c.topic, c.company || '-'],
                    }))}
                />
            )}
            {drawer && (
                <Drawer onClose={() => setDrawer(null)} title={drawer.name}>
                    <KV k="Email" v={drawer.email} />
                    <KV k="Phone" v={drawer.phone || '-'} />
                    <KV k="Company" v={drawer.company || '-'} />
                    <KV k="Topic" v={drawer.topic} />
                    <KV k="Message" v={drawer.message} multiline />
                </Drawer>
            )}
        </Section>
    );
}

// --- Clients --------------------------------------------------------------

function ClientsView() {
    const [rows, setRows] = useState(null);
    const [modal, setModal] = useState(null); // 'create' | { id }
    const reload = () => apiAdminClients().then(setRows).catch(() => setRows([]));
    useEffect(() => { reload(); }, []);
    const editing = rows?.find((r) => r.id === modal?.id);

    return (
        <Section
            title="Clients"
            subtitle={`${rows?.length || 0} client accounts`}
            action={<ToolbarBtn onClick={() => setModal('create')} testId="admin-client-create-open"><Plus size={14} /> Create Client</ToolbarBtn>}
        >
            {rows === null ? <p className="text-brand-muted">Loading…</p> : (
                <Table
                    data-testid="admin-clients-table"
                    headers={['Name', 'Company', 'Email', 'Phone', 'Last Login']}
                    rows={rows.map((c) => ({
                        key: c.id,
                        onClick: () => setModal({ id: c.id }),
                        cells: [c.name, c.company || '-', c.email, c.phone || '-', fmtDate(c.last_login_at)],
                    }))}
                />
            )}
            {modal === 'create' ? <ClientModal onClose={() => setModal(null)} onSaved={() => { reload(); setModal(null); }} /> : null}
            {editing ? <ClientModal client={editing} onClose={() => setModal(null)} onSaved={() => { reload(); setModal(null); }} /> : null}
        </Section>
    );
}

function ClientModal({ client, onClose, onSaved }) {
    const editing = !!client;
    const [form, setForm] = useState({
        name: client?.name || '',
        company: client?.company || '',
        email: client?.email || '',
        phone: client?.phone || '',
        password: '',
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setErr('');
        try {
            if (editing) {
                const body = { name: form.name, company: form.company, phone: form.phone };
                if (form.password) body.new_password = form.password;
                await apiAdminUpdateClient(client.id, body);
            } else {
                await apiAdminCreateClient({
                    email: form.email, password: form.password,
                    name: form.name, company: form.company, phone: form.phone || undefined,
                });
            }
            onSaved();
        } catch (e2) {
            setErr(e2?.response?.data?.detail || 'Failed to save');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal onClose={onClose} title={editing ? 'Edit Client' : 'Create Client'}>
            <form onSubmit={submit} className="grid gap-4" data-testid="admin-client-form">
                <Field label="Full name" value={form.name} onChange={upd('name')} required testId="admin-client-name" />
                <Field label="Company" value={form.company} onChange={upd('company')} required testId="admin-client-company" />
                <Field label="Email" type="email" value={form.email} onChange={upd('email')} required disabled={editing} testId="admin-client-email" />
                <Field label="Phone" value={form.phone} onChange={upd('phone')} testId="admin-client-phone" />
                <Field
                    label={editing ? 'Reset password (leave blank to keep)' : 'Initial password (8+ chars)'}
                    type="password" value={form.password} onChange={upd('password')}
                    required={!editing} testId="admin-client-password"
                />
                {err ? <p className="text-sm text-brand-danger">{String(err)}</p> : null}
                <SubmitBtn busy={busy} testId="admin-client-submit">{editing ? 'Save' : 'Create Client'}</SubmitBtn>
            </form>
        </Modal>
    );
}

// --- Leases ---------------------------------------------------------------

const MAT_TYPES = ['access', 'rig', 'crane'];
const LEASE_STATUSES = ['active', 'returning', 'returned'];

function LeasesView() {
    const [rows, setRows] = useState(null);
    const [clients, setClients] = useState([]);
    const [modal, setModal] = useState(null);
    const reload = () => apiAdminLeases().then(setRows).catch(() => setRows([]));
    useEffect(() => {
        reload();
        apiAdminClients().then(setClients).catch(() => setClients([]));
    }, []);
    const editing = rows?.find((r) => r.id === modal?.id);

    const onDelete = async (id) => {
        if (!window.confirm('Delete this lease?')) return;
        await apiAdminDeleteLease(id);
        reload();
    };

    return (
        <Section
            title="Leases"
            subtitle={`${rows?.length || 0} active in fleet`}
            action={<ToolbarBtn onClick={() => setModal('create')} testId="admin-lease-create-open"><Plus size={14} /> Create Lease</ToolbarBtn>}
        >
            {rows === null ? <p className="text-brand-muted">Loading…</p> : (
                <Table
                    data-testid="admin-leases-table"
                    headers={['Project', 'Client', 'Mat', 'Qty', 'Status', 'Location', 'Daily', '']}
                    rows={rows.map((l) => ({
                        key: l.id,
                        cells: [
                            <button type="button" className="text-left font-display text-sm font-bold uppercase tracking-tight text-brand-text hover:text-brand-gold" onClick={() => setModal({ id: l.id })}>
                                {l.project_name}
                            </button>,
                            <span><div>{l.client_name || '-'}</div><div className="text-xs text-brand-muted">{l.client_company || ''}</div></span>,
                            l.mat_type,
                            l.quantity?.toLocaleString(),
                            <Chip color={l.status}>{l.status}</Chip>,
                            l.location?.label || '-',
                            l.daily_rate != null ? `$${l.daily_rate.toFixed(2)}` : '-',
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onDelete(l.id); }}
                                data-testid={`admin-lease-delete-${l.id}`}
                                className="inline-flex h-8 w-8 items-center justify-center border border-brand-hairline text-brand-muted hover:border-brand-danger hover:text-brand-danger"
                            >
                                <Trash2 size={12} />
                            </button>,
                        ],
                    }))}
                />
            )}
            {modal === 'create' ? <LeaseModal clients={clients} onClose={() => setModal(null)} onSaved={() => { reload(); setModal(null); }} /> : null}
            {editing ? <LeaseModal lease={editing} clients={clients} onClose={() => setModal(null)} onSaved={() => { reload(); setModal(null); }} /> : null}
        </Section>
    );
}

function LeaseModal({ lease, clients = [], onClose, onSaved }) {
    const editing = !!lease;
    const [form, setForm] = useState(() => ({
        client_user_id: lease?.client_user_id || (clients[0]?.id || ''),
        project_name: lease?.project_name || '',
        mat_type: lease?.mat_type || 'access',
        quantity: lease?.quantity || 100,
        daily_rate: lease?.daily_rate ?? 4.5,
        deployment_date: lease?.deployment_date || '',
        expected_return: lease?.expected_return || '',
        status: lease?.status || 'active',
        location_label: lease?.location?.label || '',
        location_lat: lease?.location?.lat ?? 56.25,
        location_lng: lease?.location?.lng ?? -120.84,
        notes: lease?.notes || '',
        photos_csv: (lease?.photos || []).join('\n'),
    }));
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setErr('');
        try {
            const body = {
                client_user_id: form.client_user_id,
                project_name: form.project_name,
                mat_type: form.mat_type,
                quantity: parseInt(form.quantity, 10),
                daily_rate: form.daily_rate === '' ? null : parseFloat(form.daily_rate),
                deployment_date: form.deployment_date || null,
                expected_return: form.expected_return || null,
                status: form.status,
                location: form.location_label ? {
                    label: form.location_label,
                    lat: parseFloat(form.location_lat),
                    lng: parseFloat(form.location_lng),
                } : null,
                photos: form.photos_csv.split(/\n+/).map((s) => s.trim()).filter(Boolean),
                notes: form.notes || null,
            };
            if (editing) await apiAdminUpdateLease(lease.id, body);
            else await apiAdminCreateLease(body);
            onSaved();
        } catch (e2) {
            setErr(e2?.response?.data?.detail || 'Failed to save');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal onClose={onClose} title={editing ? 'Edit Lease' : 'Create Lease'} wide>
            <form onSubmit={submit} className="grid gap-4" data-testid="admin-lease-form">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <SelectField label="Client" value={form.client_user_id} onChange={upd('client_user_id')} required testId="admin-lease-client">
                        {clients.map((c) => <option key={c.id} value={c.id}>{c.name} - {c.company}</option>)}
                    </SelectField>
                    <Field label="Project name" value={form.project_name} onChange={upd('project_name')} required testId="admin-lease-project" />
                </div>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                    <SelectField label="Mat type" value={form.mat_type} onChange={upd('mat_type')} testId="admin-lease-mat">
                        {MAT_TYPES.map((m) => <option key={m} value={m}>{m}</option>)}
                    </SelectField>
                    <Field label="Quantity" type="number" value={form.quantity} onChange={upd('quantity')} required testId="admin-lease-qty" />
                    <Field label="Daily rate (CAD)" type="number" step="0.01" value={form.daily_rate} onChange={upd('daily_rate')} testId="admin-lease-rate" />
                    <SelectField label="Status" value={form.status} onChange={upd('status')} testId="admin-lease-status">
                        {LEASE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </SelectField>
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field label="Deployment date" type="date" value={form.deployment_date} onChange={upd('deployment_date')} testId="admin-lease-deploy" />
                    <Field label="Expected return" type="date" value={form.expected_return} onChange={upd('expected_return')} testId="admin-lease-return" />
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <Field label="Location label" value={form.location_label} onChange={upd('location_label')} testId="admin-lease-loc-label" />
                    <Field label="Lat" type="number" step="0.0001" value={form.location_lat} onChange={upd('location_lat')} testId="admin-lease-loc-lat" />
                    <Field label="Lng" type="number" step="0.0001" value={form.location_lng} onChange={upd('location_lng')} testId="admin-lease-loc-lng" />
                </div>
                <TextareaField label="Photos (one URL per line)" value={form.photos_csv} onChange={upd('photos_csv')} rows={3} testId="admin-lease-photos" />
                <TextareaField label="Notes" value={form.notes} onChange={upd('notes')} rows={3} testId="admin-lease-notes" />
                {err ? <p className="text-sm text-brand-danger">{String(err)}</p> : null}
                <SubmitBtn busy={busy} testId="admin-lease-submit">{editing ? 'Save' : 'Create Lease'}</SubmitBtn>
            </form>
        </Modal>
    );
}

// --- Hero Slides ----------------------------------------------------------

function HeroView() {
    const [rows, setRows] = useState(null);
    const [modal, setModal] = useState(null);
    const reload = () =>
        apiClient.get('/admin/hero-slides').then((r) => setRows(r.data)).catch(() => setRows([]));
    useEffect(() => { reload(); }, []);
    const editing = rows?.find((r) => r.id === modal?.id);

    const onToggle = async (s) => {
        await apiClient.patch(`/admin/hero-slides/${s.id}`, { enabled: !s.enabled });
        reload();
    };
    const onDelete = async (id) => {
        if (!window.confirm('Delete this slide?')) return;
        await apiClient.delete(`/admin/hero-slides/${id}`);
        reload();
    };
    const onMove = async (id, dir) => {
        if (!rows) return;
        const idx = rows.findIndex((r) => r.id === id);
        const j = idx + dir;
        if (j < 0 || j >= rows.length) return;
        const ordered = rows.map((r) => r.id);
        [ordered[idx], ordered[j]] = [ordered[j], ordered[idx]];
        await apiClient.post('/admin/hero-slides/reorder', { ordered_ids: ordered });
        reload();
    };

    return (
        <Section
            title="Hero Slides"
            subtitle={`${rows?.length || 0} slides - public sees enabled, sorted ascending`}
            action={<ToolbarBtn onClick={() => setModal('create')} testId="admin-hero-create-open"><Plus size={14} /> Add Slide</ToolbarBtn>}
        >
            {rows === null ? <p className="text-brand-muted">Loading…</p> : (
                <div className="grid gap-3" data-testid="admin-hero-list">
                    {rows.map((s, i) => (
                        <div key={s.id} className="grid grid-cols-[120px_1fr_auto] gap-5 border border-brand-hairline bg-brand-surface p-4">
                            <div className="aspect-[16/9] overflow-hidden border border-brand-hairline bg-brand-elevated">
                                {s.image_url ? <img src={s.image_url} alt="" className="h-full w-full object-cover" /> : null}
                            </div>
                            <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="font-display text-[10px] font-bold uppercase tracking-[0.18em] text-brand-muted">#{s.sort_order}</span>
                                    {s.eyebrow ? <span className="font-display text-[10px] font-bold uppercase tracking-[0.18em] text-brand-gold">{s.eyebrow}</span> : null}
                                    <Chip color={s.enabled ? 'active' : 'returned'}>{s.enabled ? 'Enabled' : 'Disabled'}</Chip>
                                </div>
                                <h3 className="mt-2 truncate font-display text-base font-bold uppercase tracking-tight text-brand-text">
                                    {s.headline_main} {s.headline_accent ? <span className="text-brand-gold">{s.headline_accent}</span> : null}
                                </h3>
                                {s.subtitle ? <p className="mt-1 line-clamp-2 font-sans text-xs text-brand-muted">{s.subtitle}</p> : null}
                            </div>
                            <div className="flex flex-col gap-1">
                                <button type="button" data-testid={`admin-hero-up-${i}`} onClick={() => onMove(s.id, -1)} className="inline-flex h-8 w-8 items-center justify-center border border-brand-hairline text-brand-muted hover:text-brand-text disabled:opacity-30" disabled={i === 0}>
                                    <ArrowUp size={12} />
                                </button>
                                <button type="button" data-testid={`admin-hero-down-${i}`} onClick={() => onMove(s.id, 1)} className="inline-flex h-8 w-8 items-center justify-center border border-brand-hairline text-brand-muted hover:text-brand-text disabled:opacity-30" disabled={i === rows.length - 1}>
                                    <ArrowDown size={12} />
                                </button>
                                <button type="button" data-testid={`admin-hero-toggle-${i}`} onClick={() => onToggle(s)} className="inline-flex h-8 items-center justify-center border border-brand-hairline px-2 font-display text-[9px] font-bold uppercase tracking-[0.18em] text-brand-muted hover:text-brand-text">
                                    {s.enabled ? 'Disable' : 'Enable'}
                                </button>
                                <button type="button" onClick={() => setModal({ id: s.id })} className="inline-flex h-8 items-center justify-center border border-brand-hairline px-2 font-display text-[9px] font-bold uppercase tracking-[0.18em] text-brand-text hover:text-brand-gold">
                                    Edit
                                </button>
                                <button type="button" data-testid={`admin-hero-delete-${i}`} onClick={() => onDelete(s.id)} className="inline-flex h-8 w-8 items-center justify-center border border-brand-hairline text-brand-muted hover:border-brand-danger hover:text-brand-danger">
                                    <Trash2 size={12} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {modal === 'create' ? <HeroModal onClose={() => setModal(null)} onSaved={() => { reload(); setModal(null); }} /> : null}
            {editing ? <HeroModal slide={editing} onClose={() => setModal(null)} onSaved={() => { reload(); setModal(null); }} /> : null}
        </Section>
    );
}

const ALLOWED_UPLOAD_MIME = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_UPLOAD_MB = 8;
const fmtBytes = (n) => (n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1024 / 1024).toFixed(2)} MB`);

function HeroImageInput({ value, onChange }) {
    // Dual-mode image input - "Paste URL" (default) vs "Upload File".
    // Both modes write the resulting URL into the same `image_url` field.
    const [mode, setMode] = useState('url');
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [meta, setMeta] = useState(null); // { filename, size, original_filename }
    const [err, setErr] = useState('');
    const inputRef = React.useRef(null);

    const handleFile = async (file) => {
        setErr('');
        if (!file) return;
        if (!ALLOWED_UPLOAD_MIME.includes(file.type)) {
            setErr(`Unsupported file type: ${file.type || 'unknown'}. Use JPEG, PNG, or WebP.`);
            return;
        }
        if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
            setErr(`File too large (${fmtBytes(file.size)}). Max ${MAX_UPLOAD_MB} MB.`);
            return;
        }
        setUploading(true);
        setProgress(0);
        try {
            const res = await apiAdminUploadImage(file, setProgress);
            onChange(res.url);
            setMeta({ filename: res.filename, size: res.size, original_filename: res.original_filename });
        } catch (e) {
            setErr(e?.response?.data?.detail || 'Upload failed. Try a different file.');
        } finally {
            setUploading(false);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        const f = e.dataTransfer?.files?.[0];
        if (f) handleFile(f);
    };

    const onPick = (e) => {
        const f = e.target.files?.[0];
        if (f) handleFile(f);
    };

    const resetUpload = () => {
        setMeta(null);
        setProgress(0);
        onChange('');
        if (inputRef.current) inputRef.current.value = '';
    };

    return (
        <div className="grid gap-2">
            <div className="flex items-center justify-between">
                <Label>Image</Label>
                <div className="inline-flex border border-brand-hairline" role="tablist" data-testid="admin-hero-image-mode">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={mode === 'url'}
                        data-testid="admin-hero-image-mode-url"
                        onClick={() => setMode('url')}
                        className={`px-3 py-1 font-display text-[10px] font-bold uppercase tracking-[0.18em] ${mode === 'url' ? 'bg-brand-gold text-black' : 'text-brand-muted hover:text-brand-text'}`}
                    >
                        Paste URL
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={mode === 'upload'}
                        data-testid="admin-hero-image-mode-upload"
                        onClick={() => setMode('upload')}
                        className={`border-l border-brand-hairline px-3 py-1 font-display text-[10px] font-bold uppercase tracking-[0.18em] ${mode === 'upload' ? 'bg-brand-gold text-black' : 'text-brand-muted hover:text-brand-text'}`}
                    >
                        Upload File
                    </button>
                </div>
            </div>

            {mode === 'url' ? (
                <input
                    type="text"
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    required
                    placeholder="https://… or /uploads/hero/…"
                    data-testid="admin-hero-image"
                    className="w-full border border-brand-hairline bg-brand-elevated px-3 py-2 font-sans text-sm text-brand-text outline-none focus:border-brand-gold"
                />
            ) : (
                <div
                    onDrop={onDrop}
                    onDragOver={(e) => e.preventDefault()}
                    className="flex flex-col items-center justify-center gap-2 border border-dashed border-brand-hairline bg-brand-elevated px-4 py-6 text-center"
                    data-testid="admin-hero-image-dropzone"
                >
                    {uploading ? (
                        <>
                            <Loader2 size={18} className="animate-spin text-brand-gold" />
                            <p className="font-sans text-xs text-brand-muted">Uploading… {progress}%</p>
                            <div className="h-1 w-40 overflow-hidden bg-brand-hairline">
                                <div className="h-full bg-brand-gold transition-all" style={{ width: `${progress}%` }} />
                            </div>
                        </>
                    ) : meta && value ? (
                        <>
                            <p className="font-sans text-xs text-brand-text" data-testid="admin-hero-upload-meta">
                                <span className="font-display font-bold uppercase tracking-[0.15em] text-brand-gold">Uploaded</span>{' '}
                                {meta.original_filename} · {fmtBytes(meta.size)}
                            </p>
                            <p className="font-sans text-[11px] text-brand-muted break-all">{value}</p>
                            <button
                                type="button"
                                onClick={() => inputRef.current?.click()}
                                className="font-display text-[10px] font-bold uppercase tracking-[0.18em] text-brand-gold underline underline-offset-2 hover:text-brand-gold-hover"
                                data-testid="admin-hero-upload-replace"
                            >
                                Replace
                            </button>
                        </>
                    ) : (
                        <>
                            <p className="font-display text-[11px] font-bold uppercase tracking-[0.18em] text-brand-text">
                                Drop an image here, or
                            </p>
                            <button
                                type="button"
                                onClick={() => inputRef.current?.click()}
                                className="border border-brand-gold px-3 py-1 font-display text-[10px] font-bold uppercase tracking-[0.18em] text-brand-gold hover:bg-brand-gold hover:text-black"
                                data-testid="admin-hero-upload-pick"
                            >
                                Choose File
                            </button>
                            <p className="font-sans text-[11px] text-brand-muted">JPEG, PNG, WebP · max {MAX_UPLOAD_MB} MB</p>
                        </>
                    )}
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={onPick}
                        className="hidden"
                        data-testid="admin-hero-upload-input"
                    />
                    {value && meta ? (
                        <button
                            type="button"
                            onClick={resetUpload}
                            className="font-sans text-[11px] text-brand-muted underline underline-offset-2 hover:text-brand-danger"
                            data-testid="admin-hero-upload-clear"
                        >
                            Clear
                        </button>
                    ) : null}
                </div>
            )}
            {err ? <p className="font-sans text-xs text-brand-danger" data-testid="admin-hero-upload-error">{err}</p> : null}
        </div>
    );
}



function HeroModal({ slide, onClose, onSaved }) {
    const editing = !!slide;
    const [form, setForm] = useState({
        image_url: slide?.image_url || '',
        eyebrow: slide?.eyebrow || '',
        headline_main: slide?.headline_main || '',
        headline_accent: slide?.headline_accent || '',
        subtitle: slide?.subtitle || '',
        cta_primary_label: slide?.cta_primary_label || '',
        cta_primary_href: slide?.cta_primary_href || '',
        cta_secondary_label: slide?.cta_secondary_label || '',
        cta_secondary_href: slide?.cta_secondary_href || '',
        enabled: slide?.enabled ?? true,
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setErr('');
        try {
            if (editing) await apiClient.patch(`/admin/hero-slides/${slide.id}`, form);
            else await apiClient.post('/admin/hero-slides', form);
            onSaved();
        } catch (e2) {
            setErr(e2?.response?.data?.detail || 'Failed to save');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal onClose={onClose} title={editing ? 'Edit Slide' : 'Add Slide'} wide>
            <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
                <form onSubmit={submit} className="grid gap-3" data-testid="admin-hero-form">
                    <HeroImageInput
                        value={form.image_url}
                        onChange={(v) => setForm((f) => ({ ...f, image_url: v }))}
                    />
                    <Field label="Eyebrow" value={form.eyebrow} onChange={upd('eyebrow')} testId="admin-hero-eyebrow" />
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Headline (main)" value={form.headline_main} onChange={upd('headline_main')} required testId="admin-hero-main" />
                        <Field label="Headline (accent / gold)" value={form.headline_accent} onChange={upd('headline_accent')} testId="admin-hero-accent" />
                    </div>
                    <TextareaField label="Subtitle" value={form.subtitle} onChange={upd('subtitle')} rows={2} testId="admin-hero-subtitle" />
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Primary CTA label" value={form.cta_primary_label} onChange={upd('cta_primary_label')} testId="admin-hero-cta1-label" />
                        <Field label="Primary CTA href" value={form.cta_primary_href} onChange={upd('cta_primary_href')} testId="admin-hero-cta1-href" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Secondary CTA label" value={form.cta_secondary_label} onChange={upd('cta_secondary_label')} testId="admin-hero-cta2-label" />
                        <Field label="Secondary CTA href" value={form.cta_secondary_href} onChange={upd('cta_secondary_href')} testId="admin-hero-cta2-href" />
                    </div>
                    <label className="mt-1 inline-flex items-center gap-2 font-display text-[10px] font-bold uppercase tracking-[0.18em] text-brand-text">
                        <input type="checkbox" checked={form.enabled} onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))} className="accent-[#D4A22B]" data-testid="admin-hero-enabled" />
                        Enabled (visible to public)
                    </label>
                    {err ? <p className="text-sm text-brand-danger">{String(err)}</p> : null}
                    <SubmitBtn busy={busy} testId="admin-hero-submit">{editing ? 'Save' : 'Add Slide'}</SubmitBtn>
                </form>
                <div>
                    <Label>Live preview</Label>
                    <div className="mt-2 relative aspect-[16/9] overflow-hidden border border-brand-hairline bg-brand-elevated">
                        {form.image_url ? <img src={form.image_url} alt="" className="absolute inset-0 h-full w-full object-cover" /> : null}
                        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom,#0B0B0D 0%,transparent 40%,#0B0B0D 100%)', opacity: 0.6 }} />
                        <div className="relative flex h-full flex-col justify-end p-5">
                            {form.eyebrow ? <span className="font-display text-[9px] font-bold uppercase tracking-[0.2em] text-brand-gold">{form.eyebrow}</span> : null}
                            <p className="mt-2 font-display text-xl font-extrabold uppercase tracking-tight text-brand-text">
                                {form.headline_main} {form.headline_accent ? <span className="text-brand-gold">{form.headline_accent}</span> : null}
                            </p>
                            {form.subtitle ? <p className="mt-1 line-clamp-2 font-sans text-xs text-brand-muted">{form.subtitle}</p> : null}
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
}

// --- Shared primitives ----------------------------------------------------

const Section = ({ title, subtitle, action, children }) => (
    <div>
        <div className="flex flex-wrap items-end justify-between gap-3 border-b border-brand-hairline pb-5">
            <div>
                <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight text-brand-text">{title}</h1>
                {subtitle ? <p className="mt-1 font-sans text-xs text-brand-muted">{subtitle}</p> : null}
            </div>
            {action}
        </div>
        <div className="mt-6">{children}</div>
    </div>
);

const Table = ({ headers, rows, ...rest }) => (
    <div className="overflow-x-auto border border-brand-hairline bg-brand-surface" {...rest}>
        <table className="w-full min-w-[800px] text-left text-sm">
            <thead className="border-b border-brand-hairline bg-brand-elevated">
                <tr>
                    {headers.map((h) => (
                        <th key={h} className="px-4 py-3 font-display text-[10px] font-bold uppercase tracking-[0.18em] text-brand-muted">{h}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.map((r) => (
                    <tr
                        key={r.key}
                        onClick={r.onClick}
                        className={[
                            'border-b border-brand-hairline last:border-b-0 transition-colors',
                            r.onClick ? 'cursor-pointer hover:bg-brand-elevated' : '',
                        ].join(' ')}
                    >
                        {r.cells.map((c, i) => <td key={`${r.key}-c${i}`} className="px-4 py-4 text-brand-text">{c}</td>)}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

const Chip = ({ color, children }) => {
    const cls = {
        new: 'border-brand-gold bg-brand-gold/10 text-brand-gold',
        contacted: 'border-brand-hairline text-brand-text',
        won: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400',
        lost: 'border-brand-hairline text-brand-muted',
        active: 'border-brand-gold bg-brand-gold/10 text-brand-gold',
        returning: 'border-amber-500/40 bg-amber-500/10 text-amber-400',
        returned: 'border-brand-hairline text-brand-muted',
    }[color] || 'border-brand-hairline text-brand-muted';
    return (
        <span className={`inline-flex items-center border px-2 py-1 font-display text-[10px] font-bold uppercase tracking-[0.16em] ${cls}`}>
            {children}
        </span>
    );
};

const Drawer = ({ title, children, onClose }) => (
    <div className="fixed inset-0 z-[70] flex" role="dialog" aria-modal="true" data-testid="admin-drawer">
        <button type="button" aria-label="Close" onClick={onClose} className="flex-1 bg-brand-base/70 backdrop-blur-sm" />
        <aside className="h-full w-full max-w-xl overflow-y-auto border-l border-brand-hairline bg-brand-surface p-8">
            <button type="button" onClick={onClose} className="ml-auto mb-6 flex h-10 w-10 items-center justify-center border border-brand-hairline text-brand-text" data-testid="admin-drawer-close">
                <XIcon size={16} />
            </button>
            <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-brand-text">{title}</h2>
            <div className="mt-6 space-y-4">{children}</div>
        </aside>
    </div>
);

const Modal = ({ title, children, onClose, wide = false }) => (
    <div className="fixed inset-0 z-[80] flex items-center justify-center px-4 py-8" role="dialog" aria-modal="true" data-testid="admin-modal">
        <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-brand-base/80 backdrop-blur-sm" />
        <div className={`relative max-h-full w-full overflow-y-auto border border-brand-hairline bg-brand-surface ${wide ? 'max-w-4xl' : 'max-w-lg'}`}>
            <div className="flex items-center justify-between border-b border-brand-hairline px-6 py-4">
                <h2 className="font-display text-base font-bold uppercase tracking-tight text-brand-text">{title}</h2>
                <button type="button" onClick={onClose} className="flex h-8 w-8 items-center justify-center border border-brand-hairline text-brand-text" data-testid="admin-modal-close">
                    <XIcon size={14} />
                </button>
            </div>
            <div className="p-6">{children}</div>
        </div>
    </div>
);

const KV = ({ k, v, multiline }) => (
    <div className={multiline ? '' : 'grid grid-cols-[140px_1fr] gap-4'}>
        <Label>{k}</Label>
        <p className={`font-sans text-sm text-brand-text ${multiline ? 'mt-2 whitespace-pre-line' : ''}`}>{v ?? '-'}</p>
    </div>
);

const Label = ({ children }) => (
    <span className="font-display text-[10px] font-bold uppercase tracking-[0.18em] text-brand-muted">{children}</span>
);

const Field = ({ label, testId, ...props }) => (
    <label className="block">
        <Label>{label}</Label>
        <input
            data-testid={testId}
            {...props}
            className="mt-2 w-full border border-brand-hairline bg-brand-elevated px-3 py-2 font-sans text-sm text-brand-text outline-none transition-colors focus:border-brand-gold disabled:opacity-60"
        />
    </label>
);

const SelectField = ({ label, testId, children, ...props }) => (
    <label className="block">
        <Label>{label}</Label>
        <select
            data-testid={testId}
            {...props}
            className="mt-2 w-full border border-brand-hairline bg-brand-elevated px-3 py-2 font-sans text-sm text-brand-text outline-none transition-colors focus:border-brand-gold"
        >
            {children}
        </select>
    </label>
);

const TextareaField = ({ label, testId, ...props }) => (
    <label className="block">
        <Label>{label}</Label>
        <textarea
            data-testid={testId}
            {...props}
            className="mt-2 w-full border border-brand-hairline bg-brand-elevated px-3 py-2 font-sans text-sm text-brand-text outline-none transition-colors focus:border-brand-gold"
        />
    </label>
);

const ToolbarBtn = ({ children, testId, ...rest }) => (
    <button
        type="button"
        data-testid={testId}
        {...rest}
        className="inline-flex items-center gap-2 bg-brand-gold px-4 py-2 font-display text-xs font-bold uppercase tracking-[0.16em] text-brand-base transition-colors duration-150 hover:bg-[#E5B647]"
    >
        {children}
    </button>
);

const SubmitBtn = ({ busy, children, testId }) => (
    <button
        type="submit"
        data-testid={testId}
        disabled={busy}
        className="mt-2 inline-flex items-center justify-center gap-2 bg-brand-gold px-6 py-3 font-display text-xs font-bold uppercase tracking-[0.16em] text-brand-base transition-colors duration-150 hover:bg-[#E5B647] disabled:opacity-60"
    >
        {busy ? <Loader2 className="animate-spin" size={14} /> : null}
        {children}
    </button>
);


// --- Source chip for the Leads table -------------------------------------

const SOURCE_META = {
    calculator_pdf_export: { label: 'Calculator PDF', tone: 'blue' },
    quote_form: { label: 'Quote Form', tone: 'gold' },
    contact_form: { label: 'Contact Form', tone: 'muted' },
};

const SourceChip = ({ source, quoteNumber }) => {
    const meta = SOURCE_META[source] || { label: source || 'Unknown', tone: 'muted' };
    const tone = {
        blue: 'border-brand-blue bg-brand-blue/15 text-brand-blue',
        gold: 'border-brand-gold bg-brand-gold/15 text-brand-gold',
        muted: 'border-brand-hairline bg-brand-elevated text-brand-muted',
    }[meta.tone];
    return (
        <div className="flex flex-col gap-1">
            <span
                data-testid="lead-source-chip"
                className={`inline-flex w-fit items-center border px-2 py-1 font-display text-[9px] font-bold uppercase tracking-[0.16em] ${tone}`}
            >
                {meta.label}
            </span>
            {quoteNumber ? (
                <span className="font-sans text-[10px] tabular-nums text-brand-muted">
                    {quoteNumber}
                </span>
            ) : null}
        </div>
    );
};

// --- Pricing Settings view ----------------------------------------------

function PricingView() {
    const [model, setModel] = useState(null);
    const [savedAt, setSavedAt] = useState(null);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');

    useEffect(() => {
        apiAdminGetPricing().then(setModel).catch(() => setModel({}));
    }, []);

    const set = (patch) => setModel((m) => ({ ...m, ...patch }));
    const setMatPrice = (k, v) =>
        setModel((m) => ({
            ...m,
            unit_price_per_mat: { ...(m.unit_price_per_mat || {}), [k]: Number(v) || 0 },
        }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setSaving(true); setErr('');
        try {
            const body = {
                unit_price_per_mat: model.unit_price_per_mat,
                load_capacity_mats: Number(model.load_capacity_mats) || 55,
                base_transport_per_load: Number(model.base_transport_per_load) || 0,
                fuel_surcharge_per_load: Number(model.fuel_surcharge_per_load) || 0,
                default_currency: model.default_currency || 'CAD',
                gst_rate: Number(model.gst_rate) || 0,
                pst_rate: Number(model.pst_rate) || 0,
                apply_pst: !!model.apply_pst,
                estimate_validity_days: Number(model.estimate_validity_days) || 30,
                default_salesperson_name: model.default_salesperson_name || '',
                default_salesperson_email: model.default_salesperson_email || '',
                estimate_disclaimer: model.estimate_disclaimer || '',
                spec_disclaimer: model.spec_disclaimer || '',
                pricing_public_visible: !!model.pricing_public_visible,
            };
            const updated = await apiAdminPatchPricing(body);
            setModel(updated);
            setSavedAt(new Date());
        } catch (e) {
            setErr(e?.response?.data?.detail || 'Could not save.');
        } finally {
            setSaving(false);
        }
    };

    if (!model) return <p className="text-brand-muted">Loading pricing…</p>;

    return (
        <Section
            title="Pricing Settings"
            subtitle="Drives PDF quote totals. All rates apply from next export."
        >
            <form onSubmit={onSubmit} className="space-y-8 max-w-3xl" data-testid="admin-pricing-form">
                <div>
                    <Label>Unit Price Per Mat (CAD)</Label>
                    <div className="mt-3 grid gap-4 md:grid-cols-3">
                        {['access', 'rig', 'crane'].map((k) => (
                            <div key={k}>
                                <div className="font-display text-[10px] font-bold uppercase tracking-[0.18em] text-brand-muted">
                                    {k} mats
                                </div>
                                <input
                                    type="number" step="0.01" min="0"
                                    value={model.unit_price_per_mat?.[k] ?? ''}
                                    onChange={(e) => setMatPrice(k, e.target.value)}
                                    data-testid={`pricing-unit-${k}`}
                                    className="mt-2 w-full border border-brand-hairline bg-brand-elevated px-3 py-2 font-sans tabular-nums text-brand-text outline-none focus:border-brand-gold"
                                />
                            </div>
                        ))}
                    </div>
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                    <PricingField label="Load capacity (mats/truck)" type="number"
                        value={model.load_capacity_mats} onChange={(v) => set({ load_capacity_mats: v })}
                        testId="pricing-load-capacity" />
                    <PricingField label="Transport per load (CAD)" type="number" step="0.01"
                        value={model.base_transport_per_load} onChange={(v) => set({ base_transport_per_load: v })}
                        testId="pricing-transport" />
                    <PricingField label="Fuel surcharge per load (CAD)" type="number" step="0.01"
                        value={model.fuel_surcharge_per_load} onChange={(v) => set({ fuel_surcharge_per_load: v })}
                        testId="pricing-fuel" />
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                    <PricingField label="GST rate (0.05 = 5%)" type="number" step="0.001"
                        value={model.gst_rate} onChange={(v) => set({ gst_rate: v })}
                        testId="pricing-gst" />
                    <PricingField label="PST rate (0.06 = 6%)" type="number" step="0.001"
                        value={model.pst_rate} onChange={(v) => set({ pst_rate: v })}
                        testId="pricing-pst" />
                    <div>
                        <Label>Apply PST (BC)</Label>
                        <button
                            type="button"
                            onClick={() => set({ apply_pst: !model.apply_pst })}
                            data-testid="pricing-apply-pst"
                            className={[
                                'mt-3 w-full border px-3 py-2 font-display text-xs font-bold uppercase tracking-[0.16em]',
                                model.apply_pst ? 'border-brand-gold text-brand-gold' : 'border-brand-hairline text-brand-muted',
                            ].join(' ')}
                        >
                            {model.apply_pst ? 'PST applied' : 'Off (AB)'}
                        </button>
                    </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                    <PricingField label="Salesperson name"
                        value={model.default_salesperson_name} onChange={(v) => set({ default_salesperson_name: v })}
                        testId="pricing-sales-name" />
                    <PricingField label="Salesperson email" type="email"
                        value={model.default_salesperson_email} onChange={(v) => set({ default_salesperson_email: v })}
                        testId="pricing-sales-email" />
                </div>
                <p
                    data-testid="pricing-sales-note"
                    className="-mt-2 font-sans text-xs leading-relaxed text-brand-muted"
                >
                    Defaults to <span className="text-brand-text">Tyler Hachey, Director of Sales</span> - admin can override per quote.
                </p>

                <PricingField label="Estimate validity (days)" type="number"
                    value={model.estimate_validity_days} onChange={(v) => set({ estimate_validity_days: v })}
                    testId="pricing-validity" />

                <div>
                    <Label>Estimate disclaimer</Label>
                    <textarea
                        rows={3}
                        value={model.estimate_disclaimer || ''}
                        onChange={(e) => set({ estimate_disclaimer: e.target.value })}
                        data-testid="pricing-disclaimer-quote"
                        className="mt-3 w-full border border-brand-hairline bg-brand-elevated p-3 font-sans text-sm text-brand-text outline-none focus:border-brand-gold"
                    />
                </div>
                <div>
                    <Label>Spec disclaimer</Label>
                    <textarea
                        rows={2}
                        value={model.spec_disclaimer || ''}
                        onChange={(e) => set({ spec_disclaimer: e.target.value })}
                        data-testid="pricing-disclaimer-spec"
                        className="mt-3 w-full border border-brand-hairline bg-brand-elevated p-3 font-sans text-sm text-brand-text outline-none focus:border-brand-gold"
                    />
                </div>

                {err && <p className="text-sm text-brand-danger" data-testid="pricing-error">{err}</p>}

                <div className="flex items-center gap-4">
                    <SubmitBtn busy={saving} testId="pricing-save">Save pricing</SubmitBtn>
                    {savedAt ? (
                        <span className="font-sans text-xs text-brand-muted" data-testid="pricing-saved">
                            Saved {savedAt.toLocaleTimeString('en-CA')}
                        </span>
                    ) : null}
                </div>
            </form>
        </Section>
    );
}

const PricingField = ({ label, value, onChange, type = 'text', step, testId }) => (
    <div>
        <Label>{label}</Label>
        <input
            type={type}
            step={step}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testId}
            className="mt-3 w-full border border-brand-hairline bg-brand-elevated px-3 py-2 font-sans tabular-nums text-brand-text outline-none focus:border-brand-gold"
        />
    </div>
);

// --- Export Logs view ---------------------------------------------------

function ExportLogsView() {
    const [rows, setRows] = useState(null);
    useEffect(() => {
        apiAdminExportLogs().then(setRows).catch(() => setRows([]));
    }, []);

    const totalValue = useMemo(() => {
        return (rows || []).reduce((s, r) => s + (Number(r.estimated_total) || 0), 0);
    }, [rows]);

    return (
        <Section
            title="Export Logs"
            subtitle={rows ? `${rows.length} PDF${rows.length === 1 ? '' : 's'} generated • $${totalValue.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} pipeline` : 'Loading…'}
        >
            {rows === null ? <p className="text-brand-muted">Loading…</p> : (
                <Table
                    data-testid="admin-exports-table"
                    headers={['Created', 'Quote #', 'Mode', 'Project', 'Contact', 'Mat', 'Qty', 'Loads', 'Est. Total', 'IP']}
                    rows={rows.map((r) => ({
                        key: r.id,
                        cells: [
                            fmtDate(r.created_at),
                            <span className="font-display tabular-nums tracking-[0.08em] text-brand-gold">{r.quote_number}</span>,
                            <Chip color={r.mode === 'quote' ? 'won' : 'new'}>{r.mode}</Chip>,
                            r.customer?.project_name || '-',
                            <span>
                                <div>{r.customer?.contact_name || r.customer?.company || '-'}</div>
                                <div className="text-xs text-brand-muted">{r.customer?.email || ''}</div>
                            </span>,
                            r.mat_type,
                            r.total_mats?.toLocaleString() || '-',
                            r.truckloads?.toLocaleString() || '-',
                            r.estimated_total
                                ? `$${Number(r.estimated_total).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                : '-',
                            r.ip || '-',
                        ],
                    }))}
                />
            )}
        </Section>
    );
}

// --- Users (Phase 1 auth hardening) ------------------------------------

function UsersView({ currentUser }) {
    const [rows, setRows] = useState(null);
    const [showCreate, setShowCreate] = useState(false);
    const [banner, setBanner] = useState(null); // { email, password, kind }
    const [busyId, setBusyId] = useState(null);

    const reload = () => apiAdminUsers().then(setRows).catch(() => setRows([]));
    useEffect(() => { reload(); }, []);

    const onDeactivateToggle = async (u) => {
        if (u.id === currentUser?.id) return;
        setBusyId(u.id);
        try {
            await apiAdminUpdateUser(u.id, { active: !u.active });
            await reload();
        } catch (err) {
            alert(err?.response?.data?.detail || 'Could not update user.');
        } finally {
            setBusyId(null);
        }
    };

    const onReset = async (u) => {
        if (!window.confirm(`Reset the password for ${u.email}? They will be forced to change it on next login.`)) return;
        setBusyId(u.id);
        try {
            const resp = await apiAdminResetUserPassword(u.id);
            setBanner({ email: u.email, password: resp.temp_password, kind: 'reset' });
            await reload();
        } catch (err) {
            alert(err?.response?.data?.detail || 'Could not reset password.');
        } finally {
            setBusyId(null);
        }
    };

    const onDelete = async (u) => {
        if (u.id === currentUser?.id) return;
        if (!window.confirm(`Deactivate ${u.email}? The account can be re-activated later.`)) return;
        setBusyId(u.id);
        try {
            await apiAdminDeleteUser(u.id);
            await reload();
        } catch (err) {
            alert(err?.response?.data?.detail || 'Could not deactivate user.');
        } finally {
            setBusyId(null);
        }
    };

    return (
        <Section
            title="Staff Users"
            action={
                <button
                    type="button"
                    onClick={() => setShowCreate(true)}
                    data-testid="users-create-btn"
                    className="inline-flex items-center gap-2 bg-brand-gold px-4 py-2 font-display text-xs font-bold uppercase tracking-[0.14em] text-brand-base transition-colors hover:bg-[#E5B647]"
                >
                    <Plus size={14} /> Create user
                </button>
            }
        >
            {banner ? (
                <TempPasswordBanner
                    email={banner.email}
                    password={banner.password}
                    kind={banner.kind}
                    onDismiss={() => setBanner(null)}
                />
            ) : null}
            {rows == null ? (
                <p className="text-brand-muted">Loading…</p>
            ) : (
                <Table
                    data-testid="users-table"
                    headers={['Name', 'Email', 'Role', 'Active', 'Last Login', 'Password Updated', 'Created', 'Actions']}
                    rows={rows.map((u) => ({
                        key: u.id,
                        cells: [
                            <span>
                                <div className="font-semibold text-brand-text">{u.name || '-'}</div>
                                {u.must_change_password ? (
                                    <div className="text-xs text-brand-gold">Rotation required</div>
                                ) : null}
                            </span>,
                            u.email,
                            <Chip color={u.role === 'admin' ? 'won' : 'new'}>{u.role}</Chip>,
                            <Chip color={u.active ? 'won' : 'lost'}>{u.active ? 'yes' : 'no'}</Chip>,
                            fmtDateTime(u.last_login_at),
                            fmtDateTime(u.password_updated_at),
                            fmtDate(u.created_at),
                            u.id === currentUser?.id ? (
                                <span className="font-sans text-xs text-brand-muted">(you)</span>
                            ) : (
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        type="button"
                                        disabled={busyId === u.id}
                                        onClick={() => onReset(u)}
                                        data-testid={`user-reset-${u.id}`}
                                        className="inline-flex items-center gap-1 border border-brand-hairline px-2 py-1 font-sans text-xs text-brand-text transition-colors hover:border-brand-gold hover:text-brand-gold disabled:opacity-50"
                                    >
                                        <KeyRound size={11} /> Reset
                                    </button>
                                    <button
                                        type="button"
                                        disabled={busyId === u.id}
                                        onClick={() => onDeactivateToggle(u)}
                                        data-testid={`user-toggle-${u.id}`}
                                        className="inline-flex items-center gap-1 border border-brand-hairline px-2 py-1 font-sans text-xs text-brand-text transition-colors hover:border-brand-gold hover:text-brand-gold disabled:opacity-50"
                                    >
                                        {u.active ? 'Deactivate' : 'Activate'}
                                    </button>
                                    {u.active ? (
                                        <button
                                            type="button"
                                            disabled={busyId === u.id}
                                            onClick={() => onDelete(u)}
                                            data-testid={`user-delete-${u.id}`}
                                            className="inline-flex items-center gap-1 border border-brand-hairline px-2 py-1 font-sans text-xs text-brand-text transition-colors hover:border-brand-danger hover:text-brand-danger disabled:opacity-50"
                                        >
                                            <Trash2 size={11} />
                                        </button>
                                    ) : null}
                                </div>
                            ),
                        ],
                    }))}
                />
            )}

            {showCreate ? (
                <CreateUserModal
                    onClose={() => setShowCreate(false)}
                    onCreated={({ user, temp_password }) => {
                        setBanner({ email: user.email, password: temp_password, kind: 'create' });
                        setShowCreate(false);
                        reload();
                    }}
                />
            ) : null}
        </Section>
    );
}

function CreateUserModal({ onClose, onCreated }) {
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [role] = useState('admin'); // single-tier for now; dropdown ready for expansion
    const [tempPassword, setTempPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError('');
        try {
            const body = { email: email.trim(), name: name.trim(), role };
            if (tempPassword.trim()) body.temp_password = tempPassword.trim();
            const resp = await apiAdminCreateUser(body);
            onCreated(resp);
        } catch (err) {
            const d = err?.response?.data?.detail;
            if (d?.errors) setError(`Password policy: ${d.errors.join('; ')}`);
            else if (typeof d === 'string') setError(d);
            else setError('Could not create user.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6" data-testid="create-user-modal">
            <div className="w-full max-w-md border border-brand-hairline bg-brand-surface p-6">
                <div className="flex items-center justify-between">
                    <h3 className="font-display text-base font-bold uppercase tracking-tight text-brand-text">Create staff user</h3>
                    <button type="button" onClick={onClose} data-testid="create-user-close" className="text-brand-muted hover:text-brand-text"><XIcon size={16} /></button>
                </div>
                <form onSubmit={onSubmit} className="mt-6 grid gap-4" noValidate>
                    <Field label="Full name" value={name} onChange={setName} required testId="create-user-name" />
                    <Field label="Email" value={email} onChange={setEmail} required type="email" testId="create-user-email" />
                    <label className="block">
                        <span className="font-display text-[10px] font-bold uppercase tracking-[0.2em] text-brand-muted">Role</span>
                        <select
                            value={role}
                            disabled
                            data-testid="create-user-role"
                            className="mt-2 w-full border border-brand-hairline bg-brand-elevated px-4 py-3 font-sans text-base text-brand-text outline-none"
                        >
                            <option value="admin">Admin</option>
                        </select>
                    </label>
                    <Field
                        label="Temp password (optional - auto-generated if blank)"
                        value={tempPassword}
                        onChange={setTempPassword}
                        testId="create-user-temp-pw"
                        placeholder="Leave blank for a strong auto-generated password"
                    />
                    {error ? (
                        <p className="font-sans text-sm text-brand-danger" data-testid="create-user-error">{error}</p>
                    ) : null}
                    <div className="mt-2 flex justify-end gap-3">
                        <button type="button" onClick={onClose} className="border border-brand-hairline px-4 py-2 font-display text-xs font-bold uppercase tracking-[0.14em] text-brand-muted">Cancel</button>
                        <button type="submit" disabled={submitting} data-testid="create-user-submit" className="bg-brand-gold px-4 py-2 font-display text-xs font-bold uppercase tracking-[0.14em] text-brand-base hover:bg-[#E5B647] disabled:opacity-60">
                            {submitting ? <Loader2 className="animate-spin" size={14} /> : 'Create'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function TempPasswordBanner({ email, password, kind, onDismiss }) {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(password);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        } catch { /* ignore */ }
    };
    const title = kind === 'reset' ? 'Password reset - share this with the user' : 'User created - share this with them';
    return (
        <div className="mb-6 border border-brand-gold bg-brand-elevated p-5" data-testid="temp-password-banner">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <p className="font-display text-xs font-bold uppercase tracking-[0.18em] text-brand-gold">{title}</p>
                    <p className="mt-2 font-sans text-sm text-brand-text">
                        Save this password now - it won't be shown again. {email} will be forced to change it on first login.
                    </p>
                    <code
                        data-testid="temp-password-value"
                        className="mt-3 inline-block border border-brand-hairline bg-brand-base px-4 py-2 font-mono text-base text-brand-text"
                    >
                        {password}
                    </code>
                </div>
                <div className="flex flex-col items-end gap-2">
                    <button
                        type="button"
                        onClick={copy}
                        data-testid="temp-password-copy"
                        className="inline-flex items-center gap-1 border border-brand-hairline px-3 py-2 font-display text-[10px] font-bold uppercase tracking-[0.14em] text-brand-text transition-colors hover:border-brand-gold hover:text-brand-gold"
                    >
                        <CopyIcon size={12} /> {copied ? 'Copied' : 'Copy'}
                    </button>
                    <button
                        type="button"
                        onClick={onDismiss}
                        data-testid="temp-password-dismiss"
                        className="font-display text-[10px] font-bold uppercase tracking-[0.14em] text-brand-muted hover:text-brand-text"
                    >
                        Dismiss
                    </button>
                </div>
            </div>
        </div>
    );
}

// --- Audit log --------------------------------------------------------

function AuditLogView() {
    const [rows, setRows] = useState(null);
    const [filterActor, setFilterActor] = useState('');
    const [filterAction, setFilterAction] = useState('');
    const [expanded, setExpanded] = useState({});

    const reload = () => {
        const params = {};
        if (filterActor) params.actor_email = filterActor;
        if (filterAction) params.action = filterAction;
        params.limit = 100;
        apiAdminAuditLog(params).then(setRows).catch(() => setRows([]));
    };
    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onApplyFilters = (e) => { e.preventDefault(); reload(); };

    return (
        <Section title="Audit Log (last 100 entries)">
            <form onSubmit={onApplyFilters} className="mb-6 flex flex-wrap items-end gap-3">
                <Field label="Actor email" value={filterActor} onChange={setFilterActor} testId="audit-filter-actor" />
                <Field label="Action" value={filterAction} onChange={setFilterAction} testId="audit-filter-action" placeholder="e.g. hero_slide.update" />
                <button type="submit" data-testid="audit-apply-filters" className="bg-brand-gold px-4 py-2 font-display text-xs font-bold uppercase tracking-[0.14em] text-brand-base hover:bg-[#E5B647]">
                    Apply
                </button>
            </form>
            {rows == null ? (
                <p className="text-brand-muted">Loading…</p>
            ) : rows.length === 0 ? (
                <p className="font-sans text-sm text-brand-muted">No entries match.</p>
            ) : (
                <div className="divide-y divide-brand-hairline border border-brand-hairline" data-testid="audit-log-table">
                    {rows.map((r, i) => {
                        const key = `${r.timestamp}-${i}`;
                        const isOpen = !!expanded[key];
                        return (
                            <div key={key} className="p-4" data-testid={`audit-row-${i}`}>
                                <button
                                    type="button"
                                    onClick={() => setExpanded((e) => ({ ...e, [key]: !isOpen }))}
                                    className="grid w-full grid-cols-1 gap-2 text-left md:grid-cols-[1fr_1.5fr_1.5fr_1fr_80px]"
                                >
                                    <span className="font-mono text-xs text-brand-muted">{fmtDateTime(r.timestamp)}</span>
                                    <span className="font-sans text-sm text-brand-text">{r.actor_email || '-'}</span>
                                    <span className="font-display text-xs font-bold uppercase tracking-[0.14em] text-brand-gold">{r.action}</span>
                                    <span className="font-mono text-xs text-brand-muted">{r.target_type}{r.target_id ? `/${r.target_id.slice(0, 8)}…` : ''}</span>
                                    <Chip color={r.success ? 'won' : 'lost'}>{r.success ? 'ok' : 'fail'}</Chip>
                                </button>
                                {isOpen ? (
                                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2" data-testid={`audit-row-${i}-expanded`}>
                                        <AuditJson label="before" value={r.before} />
                                        <AuditJson label="after" value={r.after} />
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            )}
        </Section>
    );
}

function AuditJson({ label, value }) {
    return (
        <div className="border border-brand-hairline bg-brand-elevated p-3">
            <span className="font-display text-[10px] font-bold uppercase tracking-[0.18em] text-brand-muted">{label}</span>
            <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-all font-mono text-xs text-brand-text">
                {value == null ? '(none)' : JSON.stringify(value, null, 2)}
            </pre>
        </div>
    );
}

// --- Content CMS (Phase 2) --------------------------------------------
//
// Shape-aware section editors. Shapes map to the Pydantic models in
// backend/content_models.py (`SECTION_MODELS`). For shapes that don't
// have a bespoke editor the fallback is a raw JSON textarea with a
// Validate button - admin can always edit, just with less hand-holding.

// --- Phase 2.6: Card-based ContentView UX ------------------------------
//
// Replaces the old left-rail + section-tabs layout with a 3-level flow:
//   Level 1: page gallery (hero-thumb cards, grouped Content + SEO)
//   Level 2: section cards for the chosen page (type-aware previews)
//   Level 3: shape-aware section editor (unchanged contract)
// Visual language mirrors the Hero Slides tab (card + thumbnail + meta).

const CONTENT_PAGE_LABEL = {
    'about': 'About Centurion',
    'projects.all': 'Projects Portfolio',
    'projects.oil-gas': 'Oil & Gas / Pipeline',
    'projects.infrastructure': 'Infrastructure Projects',
    'projects.renewables': 'Wind & Renewables',
    'partnerships.indigenous': 'Indigenous Partnerships',
    'partnerships.procurement': 'Procurement Partnerships',
    'products.access-mats': 'Access Mats Hub',
    'products.rig-mats': 'Rig Mats Hub',
    'products.crane-mats': 'Crane Mats Hub',
    'catalog': 'Product Catalog',
    'contact': 'Contact',
    'quote': 'Quote',
    'footer': 'Site Footer',
};

const SECTION_LABEL = {
    hero: 'Hero',
    story: 'Story',
    body: 'Body Copy',
    team: 'Team Members',
    trust: 'Trust Bullets',
    tiles: 'Tiles',
    applications: 'Applications',
    faq: 'FAQ',
    principles: 'Principles',
    pull_quote: 'Pull Quote',
    aeo_callout: 'AEO Callout',
    root: 'Footer Root',
    meta: 'Metadata',
};


function ContentView() {
    const [schema, setSchema] = useState(null);
    const [previews, setPreviews] = useState({}); // page_slug -> {hero data, updated_at, updated_by, sections}
    const [view, setView] = useState({ mode: 'gallery' });
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        apiAdminContentSchema().then(async (rows) => {
            setSchema(rows);
            // Fetch previews in parallel. Public /content/{slug} is fine
            // for read-only summary; admin PUT path remains gated.
            const results = await Promise.all(rows.map(async (p) => {
                try {
                    const r = await apiClient.get(`/content/${p.page_slug}`);
                    return [p.page_slug, r.data];
                } catch { return [p.page_slug, {}]; }
            }));
            // Also fetch admin-side updated_at timestamps for hero sections
            // (public endpoint only returns `data`, not metadata).
            const metas = await Promise.all(rows.map(async (p) => {
                const primary = p.sections[0]?.section_slug;
                if (!primary) return [p.page_slug, {}];
                try {
                    const r = await apiAdminGetSection(p.page_slug, primary);
                    return [p.page_slug, { updated_at: r.updated_at, updated_by: r.updated_by }];
                } catch { return [p.page_slug, {}]; }
            }));
            const next = {};
            results.forEach(([slug, data]) => { next[slug] = { data }; });
            metas.forEach(([slug, m]) => { next[slug] = { ...next[slug], ...m }; });
            setPreviews(next);
        }).catch(() => setSchema([]));
    }, [refreshKey]);

    const bumpRefresh = () => setRefreshKey((k) => k + 1);

    if (schema == null) {
        return <Section title="Site Content"><p className="text-brand-muted">Loading…</p></Section>;
    }

    const contentPages = schema.filter((p) => !p.page_slug.startsWith('seo.'));
    const seoPages = schema.filter((p) => p.page_slug.startsWith('seo.'));

    if (view.mode === 'editor') {
        const page = schema.find((p) => p.page_slug === view.pageSlug);
        const section = page?.sections.find((s) => s.section_slug === view.sectionSlug);
        return (
            <Section
                title={CONTENT_PAGE_LABEL[view.pageSlug] || view.pageSlug}
                subtitle={<ContentBreadcrumb view={view} onJump={setView} />}
            >
                {section ? (
                    <SectionEditor
                        key={`${view.pageSlug}/${view.sectionSlug}`}
                        pageSlug={view.pageSlug}
                        sectionSlug={view.sectionSlug}
                        model={section.model}
                        onSaved={bumpRefresh}
                    />
                ) : <p className="text-brand-muted">Section not found.</p>}
            </Section>
        );
    }

    if (view.mode === 'sections') {
        const page = schema.find((p) => p.page_slug === view.pageSlug);
        const preview = previews[view.pageSlug] || {};
        return (
            <Section
                title={CONTENT_PAGE_LABEL[view.pageSlug] || view.pageSlug}
                subtitle={<ContentBreadcrumb view={view} onJump={setView} />}
            >
                <div className="grid gap-4 md:grid-cols-2" data-testid="content-section-cards">
                    {page?.sections.map((s) => (
                        <SectionCard
                            key={s.section_slug}
                            pageSlug={view.pageSlug}
                            sectionSlug={s.section_slug}
                            model={s.model}
                            data={preview.data?.[s.section_slug]}
                            onEdit={() => setView({ mode: 'editor', pageSlug: view.pageSlug, sectionSlug: s.section_slug })}
                        />
                    ))}
                </div>
            </Section>
        );
    }

    // view.mode === 'gallery'
    return (
        <Section
            title="Site Content"
            subtitle={`${contentPages.length} content pages, ${seoPages.length} SEO entries. Click a page to edit its sections.`}
        >
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="content-page-gallery">
                {contentPages.map((p) => (
                    <PageCard
                        key={p.page_slug}
                        slug={p.page_slug}
                        label={CONTENT_PAGE_LABEL[p.page_slug] || p.page_slug}
                        sections={p.sections}
                        preview={previews[p.page_slug]}
                        onEdit={() => setView({ mode: 'sections', pageSlug: p.page_slug })}
                    />
                ))}
            </div>

            <div className="mt-10 border-t border-brand-hairline pt-6">
                <h3 className="font-display text-sm font-bold uppercase tracking-[0.18em] text-brand-muted">Page Metadata (SEO)</h3>
                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="content-seo-gallery">
                    {seoPages.map((p) => (
                        <SEOPageCard
                            key={p.page_slug}
                            slug={p.page_slug}
                            preview={previews[p.page_slug]}
                            onEdit={() => setView({ mode: 'editor', pageSlug: p.page_slug, sectionSlug: 'meta' })}
                        />
                    ))}
                </div>
            </div>
        </Section>
    );
}


function ContentBreadcrumb({ view, onJump }) {
    return (
        <nav className="flex items-center gap-2 font-sans text-xs text-brand-muted" data-testid="content-breadcrumb">
            <button
                type="button"
                onClick={() => onJump({ mode: 'gallery' })}
                className="hover:text-brand-gold"
                data-testid="content-breadcrumb-root"
            >
                ← All pages
            </button>
            <span>/</span>
            <button
                type="button"
                onClick={() => onJump({ mode: 'sections', pageSlug: view.pageSlug })}
                className={view.mode === 'editor' ? 'hover:text-brand-gold' : 'text-brand-text'}
            >
                {CONTENT_PAGE_LABEL[view.pageSlug] || view.pageSlug}
            </button>
            {view.mode === 'editor' ? (<>
                <span>/</span>
                <span className="text-brand-text">{SECTION_LABEL[view.sectionSlug] || view.sectionSlug}</span>
            </>) : null}
        </nav>
    );
}


function PageCard({ slug, label, sections, preview, onEdit }) {
    const heroData = preview?.data?.hero;
    const img = heroData?.image_url;
    const headline = heroData?.headline;
    return (
        <article
            className="group flex flex-col overflow-hidden border border-brand-hairline bg-brand-surface transition-colors hover:border-brand-gold"
            data-testid={`content-page-card-${slug}`}
        >
            <div className="relative aspect-[16/9] overflow-hidden bg-brand-elevated">
                {img ? (
                    <img src={img} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                ) : (
                    <div className="flex h-full items-center justify-center text-brand-muted">
                        <ImageIcon size={28} />
                    </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-brand-base/80 to-transparent" />
            </div>
            <div className="flex flex-1 flex-col gap-3 p-4">
                <div>
                    <h3 className="font-display text-base font-bold uppercase tracking-tight text-brand-text">{label}</h3>
                    <p className="mt-1 font-mono text-[11px] text-brand-muted">{slug}</p>
                </div>
                {headline ? (
                    <p className="line-clamp-2 font-sans text-xs text-brand-muted">{headline}</p>
                ) : null}
                <div className="mt-auto flex items-center justify-between gap-2 pt-2">
                    <div className="min-w-0 text-[10px] uppercase tracking-wider text-brand-muted">
                        {sections.length} section{sections.length === 1 ? '' : 's'}
                        {preview?.updated_at ? (
                            <> · updated {fmtDateTime(preview.updated_at).split(' ')[0]}
                               {preview.updated_by ? <> by {preview.updated_by.replace(/^system:/, '')}</> : null}
                            </>
                        ) : null}
                    </div>
                    <button
                        type="button"
                        onClick={onEdit}
                        data-testid={`content-page-edit-${slug}`}
                        className="shrink-0 border border-brand-gold px-3 py-1 font-display text-[10px] font-bold uppercase tracking-[0.18em] text-brand-gold hover:bg-brand-gold hover:text-brand-base"
                    >
                        Edit
                    </button>
                </div>
            </div>
        </article>
    );
}


function SEOPageCard({ slug, preview, onEdit }) {
    const meta = preview?.data?.meta || {};
    return (
        <button
            type="button"
            onClick={onEdit}
            data-testid={`content-seo-card-${slug}`}
            className="group flex min-h-[110px] flex-col gap-2 border border-brand-hairline bg-brand-surface p-3 text-left transition-colors hover:border-brand-gold"
        >
            <p className="font-mono text-[10px] text-brand-muted">{slug}</p>
            <p className="line-clamp-2 font-display text-xs font-bold text-brand-text group-hover:text-brand-gold">
                {meta.meta_title || <span className="italic text-brand-muted">(no meta title)</span>}
            </p>
            <p className="mt-auto line-clamp-2 font-sans text-[11px] text-brand-muted">
                {meta.meta_description || '(no meta description)'}
            </p>
        </button>
    );
}


function SectionCard({ pageSlug, sectionSlug, model, data, onEdit }) {
    const label = SECTION_LABEL[sectionSlug] || sectionSlug;
    return (
        <article
            className="flex flex-col gap-3 border border-brand-hairline bg-brand-surface p-4"
            data-testid={`content-section-card-${pageSlug}-${sectionSlug}`}
        >
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h3 className="font-display text-sm font-bold uppercase tracking-[0.14em] text-brand-text">{label}</h3>
                    <p className="mt-0.5 font-mono text-[10px] text-brand-muted">{model}</p>
                </div>
                <button
                    type="button"
                    onClick={onEdit}
                    data-testid={`content-section-edit-${pageSlug}-${sectionSlug}`}
                    className="shrink-0 border border-brand-gold px-3 py-1 font-display text-[10px] font-bold uppercase tracking-[0.18em] text-brand-gold hover:bg-brand-gold hover:text-brand-base"
                >
                    Edit
                </button>
            </div>
            <SectionCardPreview model={model} data={data} />
        </article>
    );
}


function SectionCardPreview({ model, data }) {
    if (!data) return <p className="font-sans text-xs italic text-brand-muted">(empty)</p>;
    switch (model) {
        case 'HeroSection': {
            const img = data.image_url;
            return (
                <div className="flex gap-3">
                    <div className="h-16 w-28 shrink-0 overflow-hidden border border-brand-hairline bg-brand-elevated">
                        {img ? <img src={img} alt="" loading="lazy" className="h-full w-full object-cover" /> : null}
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="font-display text-[10px] font-bold uppercase tracking-wider text-brand-gold">{data.eyebrow}</p>
                        <p className="mt-1 line-clamp-2 font-sans text-xs text-brand-text">{data.headline}</p>
                    </div>
                </div>
            );
        }
        case 'BodyParagraphs': {
            const first = (data.paragraphs || [])[0] || data.lead || '';
            return <p className="line-clamp-3 font-sans text-xs text-brand-muted">{first || '(empty)'}</p>;
        }
        case 'FAQSection':
        case 'ApplicationsSection':
        case 'PrinciplesSection': {
            const items = data.items || [];
            const first = items[0];
            return (
                <div className="flex flex-col gap-1">
                    <span className="font-display text-[10px] uppercase tracking-wider text-brand-muted">{items.length} entries</span>
                    {first ? (
                        <p className="line-clamp-2 font-sans text-xs text-brand-text">
                            {first.q || first.title || '(empty)'}
                        </p>
                    ) : null}
                </div>
            );
        }
        case 'TeamSection': {
            const members = data.members || [];
            return (
                <div className="flex items-center gap-2">
                    {members.slice(0, 3).map((m, i) => (
                        <div key={i} className="h-10 w-10 overflow-hidden rounded-full border border-brand-hairline bg-brand-elevated">
                            {m.photo_url ? <img src={m.photo_url} alt="" loading="lazy" className="h-full w-full object-cover" /> : null}
                        </div>
                    ))}
                    <span className="font-sans text-xs text-brand-muted">
                        {members.length} member{members.length === 1 ? '' : 's'}{members.length > 3 ? ' (+' + (members.length - 3) + ')' : ''}
                    </span>
                </div>
            );
        }
        case 'TilesSection': {
            const items = data.items || [];
            return (
                <div className="grid grid-cols-3 gap-1">
                    {items.slice(0, 3).map((t, i) => (
                        <div key={i} className="aspect-[16/10] overflow-hidden border border-brand-hairline bg-brand-elevated">
                            {t.image_url ? <img src={t.image_url} alt="" loading="lazy" className="h-full w-full object-cover" /> : (
                                <div className="flex h-full items-center justify-center"><ImageIcon size={14} className="text-brand-muted" /></div>
                            )}
                        </div>
                    ))}
                </div>
            );
        }
        case 'TrustSection': {
            const items = data.items || [];
            return <p className="font-sans text-xs text-brand-muted">{items.length} bullet{items.length === 1 ? '' : 's'}</p>;
        }
        case 'FooterSection': {
            return <p className="line-clamp-2 font-sans text-xs text-brand-muted">{data.tagline || '(no tagline)'}</p>;
        }
        case 'SEOSection': {
            return (
                <div>
                    <p className="line-clamp-1 font-display text-xs font-bold text-brand-text">{data.meta_title || '(no title)'}</p>
                    <p className="mt-1 line-clamp-2 font-sans text-[11px] text-brand-muted">{data.meta_description || '(no description)'}</p>
                </div>
            );
        }
        case 'PullQuoteSection':
        case 'AEOCalloutSection': {
            return <p className="line-clamp-2 font-sans text-xs text-brand-muted">{data.q || data.quote || '(empty)'}</p>;
        }
        default:
            return <p className="line-clamp-2 font-mono text-[11px] text-brand-muted">{JSON.stringify(data).slice(0, 120)}</p>;
    }
}

function SectionEditor({ pageSlug, sectionSlug, model, onSaved }) {
    const [row, setRow] = useState(null);
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [savedAt, setSavedAt] = useState(null);

    useEffect(() => {
        apiAdminGetSection(pageSlug, sectionSlug).then((r) => {
            setRow(r);
            setDraft(r.data ?? defaultForModel(model));
            setError('');
        });
    }, [pageSlug, sectionSlug, model]);

    const onSave = async () => {
        setSaving(true);
        setError('');
        try {
            const fresh = await apiAdminPutSection(pageSlug, sectionSlug, draft);
            setRow(fresh);
            setSavedAt(new Date());
            onSaved && onSaved();
        } catch (err) {
            const d = err?.response?.data?.detail;
            setError(typeof d === 'string' ? d : d?.errors || 'Save failed. Check the shape and try again.');
        } finally {
            setSaving(false);
        }
    };

    if (!row) return <p className="text-brand-muted">Loading section…</p>;

    const EditorCmp = EDITOR_FOR_MODEL[model] || RawJsonEditor;

    return (
        <div className="border border-brand-hairline bg-brand-surface p-6" data-testid="content-section-editor">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                    <p className="font-display text-[10px] font-bold uppercase tracking-[0.18em] text-brand-gold">
                        {pageSlug} / {sectionSlug}
                    </p>
                    <p className="mt-1 font-sans text-xs text-brand-muted">
                        Model: <code className="font-mono">{model}</code>
                        {row.updated_at ? (
                            <> • Last updated {fmtDateTime(row.updated_at)} by {row.updated_by || 'system'}</>
                        ) : null}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={onSave}
                    disabled={saving}
                    data-testid="content-save"
                    className="bg-brand-gold px-4 py-2 font-display text-xs font-bold uppercase tracking-[0.14em] text-brand-base transition-colors hover:bg-[#E5B647] disabled:opacity-60"
                >
                    {saving ? <Loader2 className="animate-spin" size={14} /> : 'Save'}
                </button>
            </div>
            <EditorCmp value={draft} onChange={setDraft} />
            {error ? (
                <p className="mt-4 font-sans text-sm text-brand-danger" data-testid="content-save-error">
                    {typeof error === 'string' ? error : JSON.stringify(error)}
                </p>
            ) : null}
            {savedAt ? (
                <p className="mt-4 font-sans text-xs text-brand-success">Saved {savedAt.toLocaleTimeString()}.</p>
            ) : null}
        </div>
    );
}

const defaultForModel = (model) => {
    switch (model) {
        case 'HeroSection': return { eyebrow: '', headline: '', body: '', image_url: null };
        case 'BodyParagraphs': return { lead: '', paragraphs: [] };
        case 'FAQSection': return { items: [] };
        case 'ApplicationsSection': return { items: [] };
        case 'TilesSection': return { items: [] };
        case 'TeamSection': return { members: [] };
        case 'TrustSection': return { items: [] };
        case 'PrinciplesSection': return { items: [] };
        case 'PullQuoteSection': return { quote: '', attribution: '' };
        case 'AEOCalloutSection': return { q: '', a: '' };
        case 'FooterSection': return {
            tagline: '', phone_display: '', phone_tel: '', email: '',
            serving: '', addresses: [], socials: [], copyright: '',
        };
        case 'SEOSection': return {
            meta_title: '', meta_description: '', canonical: '', og_image: '', json_ld: null,
        };
        default: return {};
    }
};

// --- Shape-aware editors ----------------------------------------------

function HeroEditor({ value, onChange }) {
    const v = value || {};
    return (
        <div className="grid gap-4">
            <TextInput label="Eyebrow" value={v.eyebrow || ''} onChange={(x) => onChange({ ...v, eyebrow: x })} testId="hero-eyebrow" />
            <TextInput label="Headline" value={v.headline || ''} onChange={(x) => onChange({ ...v, headline: x })} testId="hero-headline" />
            <TextArea label="Body" value={v.body || ''} onChange={(x) => onChange({ ...v, body: x })} rows={4} testId="hero-body" />
            <InlineImageField
                label="Hero image"
                value={v.image_url || ''}
                onChange={(x) => onChange({ ...v, image_url: x || null })}
                thumbVariant="tile"
                testId="hero-image-url"
            />
        </div>
    );
}

function BodyParagraphsEditor({ value, onChange }) {
    const v = value || { lead: '', paragraphs: [] };
    const setParagraph = (i, text) => {
        const next = [...(v.paragraphs || [])];
        next[i] = text;
        onChange({ ...v, paragraphs: next });
    };
    return (
        <div className="grid gap-4">
            <TextArea label="Lead paragraph" value={v.lead || ''} onChange={(x) => onChange({ ...v, lead: x })} rows={3} testId="body-lead" />
            <div className="grid gap-3">
                <span className="font-display text-[10px] font-bold uppercase tracking-[0.18em] text-brand-muted">Paragraphs</span>
                {(v.paragraphs || []).map((p, i) => (
                    <div key={i} className="grid gap-2 border border-brand-hairline bg-brand-elevated p-3" data-testid={`body-paragraph-${i}`}>
                        <TextArea label={`#${i + 1}`} value={p} onChange={(x) => setParagraph(i, x)} rows={3} />
                        <ListRowControls index={i} total={(v.paragraphs || []).length}
                            onMove={(dir) => onChange({ ...v, paragraphs: moveItem(v.paragraphs, i, dir) })}
                            onRemove={() => onChange({ ...v, paragraphs: v.paragraphs.filter((_, j) => j !== i) })}
                        />
                    </div>
                ))}
                <AddButton onClick={() => onChange({ ...v, paragraphs: [...(v.paragraphs || []), ''] })} label="Add paragraph" />
            </div>
        </div>
    );
}

function FAQEditor({ value, onChange }) {
    return <ItemListEditor
        value={value} onChange={onChange} itemsKey="items" default={{ q: '', a: '' }}
        renderItem={(item, update) => (
            <div className="grid gap-2">
                <TextInput label="Question" value={item.q || ''} onChange={(x) => update({ ...item, q: x })} />
                <TextArea label="Answer" value={item.a || ''} onChange={(x) => update({ ...item, a: x })} rows={4} />
            </div>
        )}
    />;
}

function ApplicationsEditor({ value, onChange }) {
    return <ItemListEditor
        value={value} onChange={onChange} itemsKey="items" default={{ title: '', body: '' }}
        renderItem={(item, update) => (
            <div className="grid gap-2">
                <TextInput label="Title" value={item.title || ''} onChange={(x) => update({ ...item, title: x })} />
                <TextArea label="Body" value={item.body || ''} onChange={(x) => update({ ...item, body: x })} rows={3} />
            </div>
        )}
    />;
}

function PullQuoteEditor({ value, onChange }) {
    const v = value || {};
    return (
        <div className="grid gap-4">
            <TextArea label="Quote" value={v.quote || ''} onChange={(x) => onChange({ ...v, quote: x })} rows={3} />
            <TextInput label="Attribution" value={v.attribution || ''} onChange={(x) => onChange({ ...v, attribution: x })} />
        </div>
    );
}

function AEOCalloutEditor({ value, onChange }) {
    const v = value || {};
    return (
        <div className="grid gap-4">
            <TextInput label="Question" value={v.q || ''} onChange={(x) => onChange({ ...v, q: x })} />
            <TextArea label="Answer" value={v.a || ''} onChange={(x) => onChange({ ...v, a: x })} rows={4} />
        </div>
    );
}

function TrustEditor({ value, onChange }) {
    return <ItemListEditor
        value={value} onChange={onChange} itemsKey="items" default={{ label: '' }}
        renderItem={(item, update) => (
            <TextInput label="Label" value={item.label || ''} onChange={(x) => update({ label: x })} />
        )}
    />;
}

function PrinciplesEditor({ value, onChange }) {
    return <ItemListEditor
        value={value} onChange={onChange} itemsKey="items" default={{ title: '', body: '' }}
        renderItem={(item, update) => (
            <div className="grid gap-2">
                <TextInput label="Title" value={item.title || ''} onChange={(x) => update({ ...item, title: x })} />
                <TextArea label="Body" value={item.body || ''} onChange={(x) => update({ ...item, body: x })} rows={3} />
            </div>
        )}
    />;
}

function SEOEditor({ value, onChange }) {
    const v = value || {};
    const [jsonErr, setJsonErr] = useState('');
    const jsonStr = v.json_ld == null ? '' : JSON.stringify(v.json_ld, null, 2);
    const onJsonChange = (x) => {
        if (x.trim() === '') { setJsonErr(''); onChange({ ...v, json_ld: null }); return; }
        try { const parsed = JSON.parse(x); setJsonErr(''); onChange({ ...v, json_ld: parsed }); }
        catch (e) { setJsonErr(e.message); }
    };
    return (
        <div className="grid gap-4">
            <TextInput
                label="Meta title (<= 60 chars recommended)"
                value={v.meta_title || ''}
                onChange={(x) => onChange({ ...v, meta_title: x })}
                hint={`${(v.meta_title || '').length}/60`}
            />
            <TextArea
                label="Meta description (<= 160 chars recommended)"
                value={v.meta_description || ''}
                onChange={(x) => onChange({ ...v, meta_description: x })}
                rows={3}
                hint={`${(v.meta_description || '').length}/160`}
            />
            <TextInput label="Canonical URL" value={v.canonical || ''} onChange={(x) => onChange({ ...v, canonical: x })} />
            <TextInput label="Open Graph image URL" value={v.og_image || ''} onChange={(x) => onChange({ ...v, og_image: x })} />
            <div className="grid gap-2" data-testid="seo-og-preview">
                <span className="font-display text-[10px] font-bold uppercase tracking-[0.2em] text-brand-muted">
                    Open Graph preview
                </span>
                {v.og_image ? (
                    <div className="flex gap-4">
                        <UrlThumbnail url={v.og_image} variant="og" caption={v.meta_title || 'OG image'} />
                        <div className="min-w-0 flex-1 border border-brand-hairline bg-brand-elevated p-3">
                            <p className="font-display text-sm font-semibold text-brand-text line-clamp-2">
                                {v.meta_title || '(no og:title)'}
                            </p>
                            <p className="mt-2 font-sans text-xs leading-relaxed text-brand-muted line-clamp-3">
                                {v.meta_description || '(no og:description)'}
                            </p>
                            <p className="mt-2 font-sans text-[10px] uppercase tracking-wider text-brand-muted">
                                {v.canonical || '(no canonical)'}
                            </p>
                        </div>
                    </div>
                ) : (
                    <p className="font-sans text-sm text-brand-muted" data-testid="seo-og-preview-empty">
                        No OG image set.
                    </p>
                )}
            </div>
            <label className="block">
                <span className="font-display text-[10px] font-bold uppercase tracking-[0.2em] text-brand-muted">JSON-LD</span>
                <textarea
                    defaultValue={jsonStr}
                    onBlur={(e) => onJsonChange(e.target.value)}
                    rows={10}
                    data-testid="seo-jsonld"
                    className="mt-2 w-full border border-brand-hairline bg-brand-elevated px-4 py-3 font-mono text-xs text-brand-text outline-none focus:border-brand-gold"
                    placeholder='{"@type": "Organization", "name": "Centurion"}'
                />
                {jsonErr ? <p className="mt-1 font-sans text-xs text-brand-danger">Invalid JSON: {jsonErr}</p> : null}
            </label>
        </div>
    );
}

function RawJsonEditor({ value, onChange }) {
    const [text, setText] = useState(() => JSON.stringify(value || {}, null, 2));
    const [err, setErr] = useState('');
    useEffect(() => { setText(JSON.stringify(value || {}, null, 2)); }, [value]);
    const onBlur = (e) => {
        try { const parsed = JSON.parse(e.target.value); setErr(''); onChange(parsed); }
        catch (ex) { setErr(ex.message); }
    };
    return (
        <div>
            <p className="mb-2 font-sans text-xs text-brand-muted">
                Shape-aware editor not yet built for this section - edit the raw JSON. Shape validated on save.
            </p>
            <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                onBlur={onBlur}
                rows={20}
                data-testid="raw-json-editor"
                className="w-full border border-brand-hairline bg-brand-elevated px-4 py-3 font-mono text-xs text-brand-text outline-none focus:border-brand-gold"
            />
            {err ? <p className="mt-1 font-sans text-xs text-brand-danger">Invalid JSON: {err}</p> : null}
        </div>
    );
}

// --- Phase 2.5 shape-aware editors --------------------------------------

/** Social platform options for FooterSocialLink editor. */
const SOCIAL_PLATFORMS = ['LinkedIn', 'Facebook', 'Instagram', 'X', 'YouTube', 'Other'];

function TilesEditor({ value, onChange }) {
    return <ItemListEditor
        value={value} onChange={onChange} itemsKey="items"
        default={{ eyebrow: '', title: '', body: '', link: '', image_url: '' }}
        renderItem={(item, update) => (
            <div className="grid gap-2">
                <TextInput label="Eyebrow" value={item.eyebrow || ''} onChange={(x) => update({ ...item, eyebrow: x })} />
                <TextInput label="Title" value={item.title || ''} onChange={(x) => update({ ...item, title: x })} />
                <TextArea label="Body" value={item.body || ''} onChange={(x) => update({ ...item, body: x })} rows={3} />
                <TextInput label="Link (path or URL)" value={item.link || ''} onChange={(x) => update({ ...item, link: x })} />
                <div className="flex items-end gap-3">
                    <div className="min-w-0 flex-1">
                        <ImageUrlInput label="Image URL (optional)" value={item.image_url || ''} onChange={(x) => update({ ...item, image_url: x })} />
                    </div>
                    <UrlThumbnail url={item.image_url} variant="tile" caption={item.title || 'tile preview'} />
                </div>
            </div>
        )}
    />;
}

function TeamEditor({ value, onChange }) {
    return <ItemListEditor
        value={value} onChange={onChange} itemsKey="members"
        default={{ name: '', role: '', bio: '', photo_url: '' }}
        renderItem={(item, update) => (
            <div className="grid gap-2">
                <TextInput label="Name" value={item.name || ''} onChange={(x) => update({ ...item, name: x })} />
                <TextInput label="Role" value={item.role || ''} onChange={(x) => update({ ...item, role: x })} />
                <TextArea label="Bio" value={item.bio || ''} onChange={(x) => update({ ...item, bio: x })} rows={4} />
                <div className="flex items-end gap-3">
                    <div className="min-w-0 flex-1">
                        <ImageUrlInput label="Photo URL (optional)" value={item.photo_url || ''} onChange={(x) => update({ ...item, photo_url: x })} />
                    </div>
                    <UrlThumbnail url={item.photo_url} variant="square" placeholder={UserIcon} caption={item.name || 'team photo'} />
                </div>
            </div>
        )}
    />;
}

/** Flat form (not a list) matching FooterSection with nested
 *  Addresses + Social Links sub-list editors. */
function FooterRootEditor({ value, onChange }) {
    const v = value || {
        tagline: '', phone_display: '', phone_tel: '', email: '',
        serving: '', addresses: [], socials: [], copyright: '',
    };
    const set = (k) => (x) => onChange({ ...v, [k]: x });
    return (
        <div className="grid gap-4">
            <TextInput label="Tagline" value={v.tagline || ''} onChange={set('tagline')} testId="footer-tagline" />
            <TextInput label="Copyright" value={v.copyright || ''} onChange={set('copyright')} testId="footer-copyright" />
            <div className="grid gap-4 md:grid-cols-2">
                <TextInput label="Phone (display)" value={v.phone_display || ''} onChange={set('phone_display')} />
                <TextInput label="Phone (tel: href)" value={v.phone_tel || ''} onChange={set('phone_tel')} />
            </div>
            <TextInput label="Email" value={v.email || ''} onChange={set('email')} />
            <TextInput label="Serving" value={v.serving || ''} onChange={set('serving')} />

            <div className="grid gap-2 border border-brand-hairline bg-brand-elevated p-4">
                <span className="font-display text-[10px] font-bold uppercase tracking-[0.18em] text-brand-muted">Addresses</span>
                <AddressesSubEditor value={v.addresses || []} onChange={set('addresses')} />
            </div>

            <div className="grid gap-2 border border-brand-hairline bg-brand-elevated p-4">
                <span className="font-display text-[10px] font-bold uppercase tracking-[0.18em] text-brand-muted">Social Links</span>
                <SocialLinksSubEditor value={v.socials || []} onChange={set('socials')} />
            </div>
        </div>
    );
}

function AddressesSubEditor({ value, onChange }) {
    const items = value || [];
    const defaultItem = { label: '', city: '', region: '', address: '' };
    const update = (i, next) => { const l = [...items]; l[i] = next; onChange(l); };
    return (
        <div className="grid gap-3">
            {items.length === 0 ? (
                <p className="font-sans text-sm text-brand-muted">No addresses yet.</p>
            ) : items.map((it, i) => (
                <div key={i} className="grid gap-2 border border-brand-hairline bg-brand-surface p-3" data-testid={`footer-address-${i}`}>
                    <div className="grid gap-2 md:grid-cols-2">
                        <TextInput label="Label" value={it.label || ''} onChange={(x) => update(i, { ...it, label: x })} />
                        <TextInput label="City" value={it.city || ''} onChange={(x) => update(i, { ...it, city: x })} />
                    </div>
                    <div className="grid gap-2 md:grid-cols-2">
                        <TextInput label="Region" value={it.region || ''} onChange={(x) => update(i, { ...it, region: x })} />
                        <TextInput label="Street / mailing address" value={it.address || ''} onChange={(x) => update(i, { ...it, address: x })} />
                    </div>
                    <ListRowControls
                        index={i} total={items.length}
                        onMove={(dir) => onChange(moveItem(items, i, dir))}
                        onRemove={() => onChange(items.filter((_, j) => j !== i))}
                    />
                </div>
            ))}
            <AddButton label="Add address" onClick={() => onChange([...items, { ...defaultItem }])} />
        </div>
    );
}

function SocialLinksSubEditor({ value, onChange }) {
    const items = value || [];
    const defaultItem = { platform: 'LinkedIn', url: '', enabled: true };
    const update = (i, next) => { const l = [...items]; l[i] = next; onChange(l); };
    return (
        <div className="grid gap-3">
            {items.length === 0 ? (
                <p className="font-sans text-sm text-brand-muted">No social links yet.</p>
            ) : items.map((it, i) => (
                <div key={i} className="grid gap-2 border border-brand-hairline bg-brand-surface p-3" data-testid={`footer-social-${i}`}>
                    <div className="grid gap-2 md:grid-cols-2">
                        <label className="block">
                            <span className="font-display text-[10px] font-bold uppercase tracking-[0.2em] text-brand-muted">Platform</span>
                            <select
                                value={it.platform || 'LinkedIn'}
                                onChange={(e) => update(i, { ...it, platform: e.target.value })}
                                data-testid={`footer-social-platform-${i}`}
                                className="mt-2 w-full border border-brand-hairline bg-brand-elevated px-4 py-3 font-sans text-base text-brand-text outline-none focus:border-brand-gold"
                            >
                                {SOCIAL_PLATFORMS.map((p) => (<option key={p} value={p}>{p}</option>))}
                            </select>
                        </label>
                        <TextInput label="URL" value={it.url || ''} onChange={(x) => update(i, { ...it, url: x })} />
                    </div>
                    <label className="inline-flex items-center gap-2 font-sans text-sm text-brand-text">
                        <input
                            type="checkbox"
                            checked={it.enabled !== false}
                            onChange={(e) => update(i, { ...it, enabled: e.target.checked })}
                            data-testid={`footer-social-enabled-${i}`}
                        />
                        Enabled
                    </label>
                    <ListRowControls
                        index={i} total={items.length}
                        onMove={(dir) => onChange(moveItem(items, i, dir))}
                        onRemove={() => onChange(items.filter((_, j) => j !== i))}
                    />
                </div>
            ))}
            <AddButton label="Add social link" onClick={() => onChange([...items, { ...defaultItem }])} />
        </div>
    );
}

/** URL input with a "Browse Hero Slides" link-out that opens the admin
 *  uploads tab in a new window - inline upload-per-editor is deferred to
 *  Phase 3+ per Phase 2.5 scope. */
function ImageUrlInput({ label, value, onChange }) {
    // Phase 2.6: now a thin wrapper around InlineImageField so every
    // image URL field in the CMS gets the inline-upload affordance.
    return <InlineImageField label={label} value={value} onChange={onChange} />;
}


// Phase 2.6: unified image URL field with inline upload modal. Replaces
// the earlier "paste URL + link out to Hero Slides tab" affordance.
// Reuses `apiAdminUploadImage` so Team / Tile / Hero / OG uploads all
// land in the same Emergent Files bucket as Hero Slides uploads.
function InlineImageField({ label, value, onChange, thumbVariant = 'tile', testId }) {
    const [showModal, setShowModal] = useState(false);
    const err = value && !(value.startsWith('/api/') || value.startsWith('https://') || value.startsWith('/')) ?
        'Image URL should start with /api/files/ or https://' : '';
    return (
        <div>
            <div className="flex items-end gap-3">
                <label className="block min-w-0 flex-1">
                    <span className="font-display text-[10px] font-bold uppercase tracking-[0.2em] text-brand-muted">
                        {label}
                    </span>
                    <input
                        type="text"
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder="/api/files/... or https://..."
                        data-testid={testId}
                        className="mt-2 w-full border border-brand-hairline bg-brand-elevated px-4 py-3 font-sans text-base text-brand-text outline-none focus:border-brand-gold"
                    />
                </label>
                <UrlThumbnail url={value || ''} variant={thumbVariant} />
            </div>
            {err ? <p className="mt-1 font-sans text-xs text-brand-danger">{err}</p> : null}
            <div className="mt-2 flex items-center justify-between gap-3">
                <button
                    type="button"
                    onClick={() => setShowModal(true)}
                    data-testid={testId ? `${testId}-upload-open` : 'inline-image-upload-open'}
                    className="inline-flex items-center gap-2 border border-brand-gold px-3 py-1 font-display text-[10px] font-bold uppercase tracking-[0.18em] text-brand-gold hover:bg-brand-gold hover:text-brand-base"
                >
                    <Plus size={10} /> Upload new
                </button>
                {value ? (
                    <button
                        type="button"
                        onClick={() => onChange('')}
                        data-testid={testId ? `${testId}-clear` : 'inline-image-clear'}
                        className="font-sans text-[11px] text-brand-muted underline-offset-2 hover:text-brand-danger hover:underline"
                    >
                        Clear
                    </button>
                ) : null}
            </div>
            {showModal ? (
                <InlineImageUploadModal
                    onClose={() => setShowModal(false)}
                    onUploaded={(url) => { onChange(url); setShowModal(false); }}
                    testIdPrefix={testId || 'inline-image'}
                />
            ) : null}
        </div>
    );
}


function InlineImageUploadModal({ onClose, onUploaded, testIdPrefix }) {
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [err, setErr] = useState('');
    const [preview, setPreview] = useState(null); // object URL preview for selected file
    const fileRef = React.useRef(null);

    const pick = async (file) => {
        setErr('');
        if (!file) return;
        if (!ALLOWED_UPLOAD_MIME.includes(file.type)) {
            setErr(`Unsupported file type. Use JPEG, PNG, or WebP.`);
            return;
        }
        if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
            setErr(`File too large (${fmtBytes(file.size)}). Max ${MAX_UPLOAD_MB} MB.`);
            return;
        }
        setPreview(URL.createObjectURL(file));
        setUploading(true);
        setProgress(0);
        try {
            const res = await apiAdminUploadImage(file, setProgress);
            onUploaded(res.url);
        } catch (e) {
            setErr(e?.response?.data?.detail || 'Upload failed. Try a different file.');
        } finally {
            setUploading(false);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        const f = e.dataTransfer?.files?.[0];
        if (f) pick(f);
    };

    return (
        <Modal onClose={onClose} title="Upload Image">
            <div className="grid gap-4" data-testid={`${testIdPrefix}-upload-modal`}>
                <div
                    onDrop={onDrop}
                    onDragOver={(e) => e.preventDefault()}
                    className="flex flex-col items-center justify-center gap-3 border border-dashed border-brand-hairline bg-brand-elevated px-4 py-10 text-center"
                    data-testid={`${testIdPrefix}-upload-dropzone`}
                >
                    {uploading ? (
                        <>
                            {preview ? <img src={preview} alt="preview" className="max-h-24 object-contain opacity-60" /> : null}
                            <Loader2 size={20} className="animate-spin text-brand-gold" />
                            <p className="font-sans text-xs text-brand-muted">Uploading… {progress}%</p>
                            <div className="h-1 w-56 overflow-hidden bg-brand-hairline">
                                <div className="h-full bg-brand-gold transition-all" style={{ width: `${progress}%` }} />
                            </div>
                        </>
                    ) : (
                        <>
                            <ImageIcon size={28} className="text-brand-muted" />
                            <p className="font-display text-[11px] font-bold uppercase tracking-[0.18em] text-brand-text">Drop an image here, or</p>
                            <button
                                type="button"
                                onClick={() => fileRef.current?.click()}
                                className="border border-brand-gold px-3 py-1 font-display text-[10px] font-bold uppercase tracking-[0.18em] text-brand-gold hover:bg-brand-gold hover:text-brand-base"
                                data-testid={`${testIdPrefix}-upload-pick`}
                            >
                                Choose File
                            </button>
                            <p className="font-sans text-[11px] text-brand-muted">JPEG, PNG, WebP · max {MAX_UPLOAD_MB} MB</p>
                        </>
                    )}
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) pick(f); }}
                        className="hidden"
                        data-testid={`${testIdPrefix}-upload-input`}
                    />
                </div>
                {err ? <p className="font-sans text-xs text-brand-danger" data-testid={`${testIdPrefix}-upload-error`}>{err}</p> : null}
            </div>
        </Modal>
    );
}

// Phase 2.5 follow-up: inline thumbnail preview for URL-based image fields.
// READ-ONLY preview - the URL input stays the primary editing surface and
// inline upload is deferred per scope. Debounced 400ms so each keystroke
// doesn't trigger a thrash of image loads. Click thumbnail opens the full
// image in a new tab for quality verification.
function UrlThumbnail({ url, variant = 'square', placeholder: Placeholder = ImageIcon, caption }) {
    const [debounced, setDebounced] = useState(url || '');
    const [failed, setFailed] = useState(false);
    useEffect(() => {
        setFailed(false);
        const t = setTimeout(() => setDebounced(url || ''), 400);
        return () => clearTimeout(t);
    }, [url]);

    const sizeClass = {
        square: 'h-16 w-16 rounded-full',  // 64x64 TeamEditor
        tile: 'h-[60px] w-[96px]',         // 96x60 landscape TilesEditor
        og: 'h-[105px] w-[200px]',         // 200x105 OG card SEOEditor
    }[variant];

    const looksValid = debounced && (debounced.startsWith('/') || /^https?:\/\//i.test(debounced));

    if (!looksValid) {
        return (
            <div
                data-testid={`thumb-${variant}-empty`}
                className={`${sizeClass} shrink-0 flex items-center justify-center border border-brand-hairline bg-brand-elevated text-brand-muted`}
            >
                <Placeholder size={variant === 'og' ? 32 : 20} aria-hidden="true" />
                {caption ? (
                    <span className="sr-only">{caption}</span>
                ) : null}
            </div>
        );
    }
    if (failed) {
        return (
            <div
                data-testid={`thumb-${variant}-failed`}
                className={`${sizeClass} shrink-0 flex flex-col items-center justify-center gap-1 border border-brand-danger bg-brand-elevated px-2 text-center`}
            >
                <XIcon size={variant === 'og' ? 24 : 16} className="text-brand-danger" aria-hidden="true" />
                <span className="font-sans text-[9px] uppercase tracking-wider text-brand-danger">
                    Image failed to load
                </span>
            </div>
        );
    }
    return (
        <a
            href={debounced}
            target="_blank"
            rel="noreferrer"
            title="Open full-size in new tab"
            data-testid={`thumb-${variant}-link`}
            className={`${sizeClass} shrink-0 block overflow-hidden border border-brand-hairline bg-brand-elevated transition-opacity duration-150 hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold`}
        >
            <img
                src={debounced}
                alt={caption || 'preview'}
                loading="lazy"
                onError={() => setFailed(true)}
                className="h-full w-full object-cover"
            />
        </a>
    );
}

// --- List primitives ---------------------------------------------------

function ItemListEditor({ value, onChange, itemsKey = 'items', default: defaultItem, renderItem }) {
    const items = (value && value[itemsKey]) || [];
    const update = (i, next) => {
        const list = [...items]; list[i] = next;
        onChange({ ...(value || {}), [itemsKey]: list });
    };
    return (
        <div className="grid gap-3">
            {items.length === 0 ? (
                <p className="font-sans text-sm text-brand-muted">No items yet.</p>
            ) : items.map((it, i) => (
                <div key={i} className="grid gap-2 border border-brand-hairline bg-brand-elevated p-3" data-testid={`list-item-${i}`}>
                    {renderItem(it, (next) => update(i, next))}
                    <ListRowControls
                        index={i}
                        total={items.length}
                        onMove={(dir) => onChange({ ...(value || {}), [itemsKey]: moveItem(items, i, dir) })}
                        onRemove={() => onChange({ ...(value || {}), [itemsKey]: items.filter((_, j) => j !== i) })}
                    />
                </div>
            ))}
            <AddButton
                label="Add item"
                onClick={() => onChange({ ...(value || {}), [itemsKey]: [...items, { ...defaultItem }] })}
            />
        </div>
    );
}

function ListRowControls({ index, total, onMove, onRemove }) {
    return (
        <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => onMove(-1)} disabled={index === 0}
                className="border border-brand-hairline px-2 py-1 text-brand-text disabled:opacity-30">
                <ArrowUp size={11} />
            </button>
            <button type="button" onClick={() => onMove(1)} disabled={index === total - 1}
                className="border border-brand-hairline px-2 py-1 text-brand-text disabled:opacity-30">
                <ArrowDown size={11} />
            </button>
            <button type="button" onClick={onRemove}
                className="border border-brand-hairline px-2 py-1 text-brand-text hover:border-brand-danger hover:text-brand-danger">
                <Trash2 size={11} />
            </button>
        </div>
    );
}

function AddButton({ label, onClick }) {
    return (
        <button type="button" onClick={onClick}
            data-testid="list-add-button"
            className="inline-flex items-center gap-1 self-start border border-brand-hairline px-3 py-2 font-display text-[10px] font-bold uppercase tracking-[0.14em] text-brand-text transition-colors hover:border-brand-gold hover:text-brand-gold">
            <Plus size={12} /> {label}
        </button>
    );
}

const moveItem = (list, i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return list;
    const next = [...list]; [next[i], next[j]] = [next[j], next[i]]; return next;
};

const TextInput = ({ label, value, onChange, hint, testId }) => (
    <label className="block">
        <span className="flex items-center justify-between font-display text-[10px] font-bold uppercase tracking-[0.2em] text-brand-muted">
            {label}
            {hint ? <span className="font-sans text-brand-muted/70 lowercase tracking-normal">{hint}</span> : null}
        </span>
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
            data-testid={testId}
            className="mt-2 w-full border border-brand-hairline bg-brand-elevated px-4 py-3 font-sans text-base text-brand-text outline-none focus:border-brand-gold" />
    </label>
);

const TextArea = ({ label, value, onChange, rows = 3, hint, testId }) => (
    <label className="block">
        <span className="flex items-center justify-between font-display text-[10px] font-bold uppercase tracking-[0.2em] text-brand-muted">
            {label}
            {hint ? <span className="font-sans text-brand-muted/70 lowercase tracking-normal">{hint}</span> : null}
        </span>
        <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={rows}
            data-testid={testId}
            className="mt-2 w-full border border-brand-hairline bg-brand-elevated px-4 py-3 font-sans text-sm text-brand-text outline-none focus:border-brand-gold" />
    </label>
);

const EDITOR_FOR_MODEL = {
    HeroSection: HeroEditor,
    BodyParagraphs: BodyParagraphsEditor,
    FAQSection: FAQEditor,
    ApplicationsSection: ApplicationsEditor,
    PullQuoteSection: PullQuoteEditor,
    AEOCalloutSection: AEOCalloutEditor,
    TrustSection: TrustEditor,
    PrinciplesSection: PrinciplesEditor,
    SEOSection: SEOEditor,
    // Phase 2.5 shape-aware editors.
    TilesSection: TilesEditor,
    TeamSection: TeamEditor,
    FooterSection: FooterRootEditor,
    // RawJsonEditor is retained as the defensive fallback for any
    // future unknown model, but no currently-registered section hits it.
};
